import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { formatRelativeTime } from '../../lib/relativeTime'
import { parseDocumentAuto } from '../../api/documentParser'
import { parseInvoiceWithClaude } from '../../api/claudeInvoiceParser'
import { matchTemplateByCustomerOrKeywords, type DocumentTemplate } from '../../api/templateManager'
import * as XLSX from 'xlsx'
import {
  getAutoJobsDriveToken,
  saveAutoJobsDriveToken,
  clearAutoJobsDriveToken,
  initiateAutoJobsOAuth,
  parseTokenFromHash,
  listFolderFiles,
  downloadFileAsBase64,
  extractFolderIdFromUrl,
  type DriveFileItem
} from '../../lib/api/google-drive-autojobs'
import DriveGroupingPanel from './DriveGroupingPanel'
import SearchableSelect from '../../components/SearchableSelect'
import MBLHBLTable from '../../components/AutoJobs/MBLHBLTable'
import type { DetailRow, Address } from '../../components/AutoJobs/MBLHBLTable'
import AddressValidationStep from './AutoJobs/AddressValidationStep'
import JobCreationConfirmDialog, { type JobCreationPrefill } from './AutoJobs/JobCreationConfirmDialog'
import JobCreationResultStep from './AutoJobs/JobCreationResultStep'
import SyncPreflightModal from '../../components/SyncPreflightModal'
import { BOXTOP_SYNC_ENABLED } from '../../lib/boxtopSync'
import { supportsLocalUpload } from '../../lib/autoJobsModes'

type Step = 'mode' | 'job_ref_preview' | 'format' | 'upload' | 'drive_input' | 'drive_preview' | 'drive_confirm' | 'review' | 'hbl_summary' | 'jobref' | 'address_validation' | 'job_confirm' | 'job_result' | 'creating' | 'done'
type ProcessingMode = 'single' | 'mbl_hbl' | 'bundled' | 'manifest' | 'invoices'
type DataSource = 'local' | 'drive'

// Per-file status icons for the bundled MBL+HBLs parse progress line
// (e.g. "MBL: ✓  |  HBL 1: ✓  |  HBL 2: ⏳  |  HBL 3: …").
const PARSE_STATUS_ICON: Record<'waiting' | 'parsing' | 'done' | 'error', string> = {
  waiting: '…',
  parsing: '⏳',
  done:    '✓',
  error:   '⚠',
}

interface ExtractedData {
  waybill_number?: string
  mbl_number?: string
  voyage_number?: string
  carrier?: string
  vessel_name?: string
  export_reference?: string
  transport_mode?: string
  direction?: string
  doc_type?: string
  flight_vessel?: string
  origin_country?: string
  dest_country?: string
  eta?: string
  etd?: string
  shipper?: { name?: string; address?: string }
  consignee?: { name?: string; address?: string; contact?: string; eori?: string; vat_no?: string }
  ports?: { loading?: string; discharge?: string; delivery?: string }
  container?: { number?: string; seal?: string; type?: string; packages?: string }
  cargo?: {
    description?: string
    pieces?: number
    gross_weight_kgs?: number
    net_weight_kgs?: number
    volume_cbm?: number
    chargeable_weight?: number
  }
  summary?: string
  // #297: per-HBL freight term (Incoterm code, e.g. "FOB") — resolved to shipping_terms_id
  // per child job by create-autojobs, mirroring how container.type resolves container_type_id.
  shipping_terms?: string
  // #13: Prepaid/Collect — written directly to job_sea_details.freight_type (already a plain
  // column, not a lookup table like shipping_terms).
  freight_type?: string
}

interface HBLData {
  sheetName: string
  pageNumber?: number
  hblNumber?: string
  hawbNumber?: string
  shipper?: string
  consignee?: string
  consigneeEori?: string
  consigneeAddress?: string
  vessel?: string
  ports?: { loading?: string; discharge?: string; finalDestination?: string }
  cargoDescription?: string
  packages?: string
  containerNumber?: string
  containerType?: string
  sealNo?: string
  grossWeightKgs?: number
  volumeCbm?: number
  onBoardDate?: string
  pva?: boolean
  isLocal?: boolean
  pieces?: number
  weight?: number
  mawbNumber?: string
  origin?: string
  destination?: string
  flightDate?: string
}

interface DriveFileWithRole extends DriveFileItem {
  role: 'mbl' | 'hbl' | 'main' | 'sub' | 'other'
}

interface DriveJobGroup {
  id: string
  files: DriveFileWithRole[]
}

const PROCESSING_MODES = [
  {
    id: 'single',
    label: 'Single Document',
    description: 'One MBL or HBL',
    fileTypes: '.pdf',
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 'mbl_hbl',
    label: 'MBL + HBL',
    description: 'Upload MBL and HBLs sep.',
    fileTypes: '.pdf (multi)',
    color: 'from-purple-500 to-purple-600'
  },
  {
    id: 'bundled',
    label: 'MBL + Bundled HBLs',
    description: 'Excel/PDF bundling HBLs',
    fileTypes: '.xlsx, .pdf',
    color: 'from-green-500 to-green-600'
  },
  {
    id: 'manifest',
    label: 'MBL + Manifest',
    description: 'MAWB + HAWBs / W-Receipt',
    fileTypes: '.xls, .xlsx',
    color: 'from-orange-500 to-orange-600'
  },
  {
    id: 'invoices',
    label: 'MBL + HBLs from Invoices',
    description: 'Commercial invoices',
    fileTypes: '.xlsx (multi)',
    color: 'from-pink-500 to-pink-600'
  }
]

function parseAddressString(addr?: string): Address | undefined {
  if (!addr) return undefined
  const parts = addr.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
  return {
    line1: parts[0],
    line2: parts[1],
    city: parts[2],
    postcode: parts[3],
    country: parts[4],
  }
}

// Z43: HBL/BL text writes container size/type with formatting container_types.code never has --
// a foot-mark quote ("40'HQ"), a space ("40 HC"), or the industry-common "HC" (High Cube)
// abbreviation where our codes use "HQ" (20GP/40GP/40FR/40HQ/40OT/40RF/45HQ/LCL). Left as-is, the
// Container Type <select> in the review table can never match an <option>, so it silently shows
// blank ("Select type...") even though the AI extracted a perfectly good value. Mirrors
// create-autojobs' own normalizeContainerTypeCode so the review table's default matches what the
// job will actually be created with.
function normalizeContainerTypeCode(raw?: string | null): string {
  if (!raw) return ''
  return raw.toUpperCase().replace(/['"\s-]/g, '').replace(/HC$/, 'HQ')
}

function extractedToDetailRow(data: ExtractedData, id: string): DetailRow {
  return {
    id,
    document: data.doc_type || '',
    waybill: typeof data.waybill_number === 'string' ? data.waybill_number : (data.waybill_number ? String(data.waybill_number) : ''),
    mblNo: typeof data.mbl_number === 'string' ? data.mbl_number : (data.mbl_number ? String(data.mbl_number) : ''),
    voyageNo: typeof data.voyage_number === 'string' ? data.voyage_number : (data.voyage_number ? String(data.voyage_number) : ''),
    carrier: data.carrier || '',
    vessel: data.vessel_name || data.flight_vessel || '',
    exportRef: data.export_reference || '',
    shipper: data.shipper?.name || '',
    shipperAddress: parseAddressString(data.shipper?.address),
    consignee: data.consignee?.name || '',
    consigneeAddress: parseAddressString(data.consignee?.address),
    consigneeContact: data.consignee?.contact || '',
    consigneeEori: '',
    portLoading: data.ports?.loading || '',
    portDischarge: data.ports?.discharge || '',
    finalDelivery: data.ports?.delivery || '',
    containerNo: typeof data.container?.number === 'string' ? data.container.number : (data.container?.number ? String(data.container.number) : ''),
    sealNo: typeof data.container?.seal === 'string' ? data.container.seal : (data.container?.seal ? String(data.container.seal) : ''),
    containerType: normalizeContainerTypeCode(data.container?.type),
    shippingTerms: data.shipping_terms || '',
    freightType: data.freight_type || '',
    packages: typeof data.container?.packages === 'string' ? data.container.packages : (data.container?.packages ? String(data.container.packages) : (data.cargo?.pieces ? String(data.cargo.pieces) : '')),
    grossWeightKgs: data.cargo?.gross_weight_kgs,
    netWeightKgs: data.cargo?.net_weight_kgs,
    volumeCbm: data.cargo?.volume_cbm,
    cargoDescription: data.cargo?.description || '',
    summary: data.summary || '',
    isAiInferred: {},
    edited: {},
  }
}

function addressToString(addr?: Address): string | undefined {
  if (!addr) return undefined
  const joined = [addr.line1, addr.line2, addr.city, addr.postcode, addr.country].filter(Boolean).join(', ')
  return joined || undefined
}

// Z58.9: this used to be `${prefix}-${Date.now()}` (e.g. "CONS-1781214332891") -- a 13-digit
// unix timestamp suffix that's indistinguishable from a real BoxTop account number once it
// lands in customers.code and gets truncated into boxtop_account_number by
// deriveBoxtopAccountNumber (sync-to-boxtrax). Matches the initials+random-digits convention
// already used by CreateCustomerDialog/QuickAddCustomerDialog's generateCode.
function generateFallbackCustomerCode(name: string, prefix: string): string {
  const initials = name.split(/\s+/).map(w => w[0]).filter(Boolean).join('').toUpperCase().substring(0, 8)
  const randomNum = Math.floor(1000 + Math.random() * 9000)
  return `${initials || prefix}${randomNum}`
}

// #297: reverse of extractedToDetailRow — closes the "MBLHBLTable edits never sync back"
// gap (onDataChange was previously a no-op stub) so edits made in the review table, including
// the new per-row Container Type / Freight Term dropdowns, actually reach job creation.
// `original` supplies fields DetailRow doesn't carry (e.g. origin_country, cargo.pieces).
function detailRowToExtracted(row: DetailRow, original?: ExtractedData): ExtractedData {
  return {
    ...original,
    doc_type: row.document || original?.doc_type,
    waybill_number: row.waybill || undefined,
    mbl_number: row.mblNo || undefined,
    voyage_number: row.voyageNo || undefined,
    carrier: row.carrier || undefined,
    vessel_name: row.vessel || original?.vessel_name,
    export_reference: row.exportRef || undefined,
    shipper: {
      name: row.shipper || original?.shipper?.name,
      address: addressToString(row.shipperAddress) ?? original?.shipper?.address,
    },
    consignee: {
      name: row.consignee || original?.consignee?.name,
      address: addressToString(row.consigneeAddress) ?? original?.consignee?.address,
      contact: row.consigneeContact || original?.consignee?.contact,
    },
    ports: {
      loading: row.portLoading || original?.ports?.loading,
      discharge: row.portDischarge || original?.ports?.discharge,
      delivery: row.finalDelivery || original?.ports?.delivery,
    },
    container: {
      number: row.containerNo || original?.container?.number,
      seal: row.sealNo || original?.container?.seal,
      type: row.containerType || original?.container?.type,
      packages: row.packages || original?.container?.packages,
    },
    cargo: {
      ...original?.cargo,
      description: row.cargoDescription || original?.cargo?.description,
      gross_weight_kgs: row.grossWeightKgs ?? original?.cargo?.gross_weight_kgs,
      net_weight_kgs: row.netWeightKgs ?? original?.cargo?.net_weight_kgs,
      volume_cbm: row.volumeCbm ?? original?.cargo?.volume_cbm,
    },
    summary: row.summary || original?.summary,
    shipping_terms: row.shippingTerms || original?.shipping_terms,
    freight_type: row.freightType || original?.freight_type,
  }
}

// Z41.5: PostgREST caps a query at 1000 rows by default. Reference tables like `ports` (41k+
// rows) silently truncate past that cap, so a plain .select() only ever returns the first page
// in sort order. Pages through with .range() -- queryFactory must build a *fresh* query each
// call since a query builder can't be re-awaited after its first .range() call.
async function fetchAllRows<T>(
  queryFactory: () => { range: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }> },
  pageSize = 1000
): Promise<{ data: T[]; error: unknown }> {
  const allRows: T[] = []
  let page = 0
  while (true) {
    const from = page * pageSize
    const { data, error } = await queryFactory().range(from, from + pageSize - 1)
    if (error) return { data: allRows, error }
    if (!data || data.length === 0) break
    allRows.push(...data)
    if (data.length < pageSize) break
    page++
  }
  return { data: allRows, error: null }
}

export default function AutoJobsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { userEmail } = useAuth()
  // Z36 redo: arriving from a pre-reservation ("Use this" -> "Create from a document") carries the
  // reservation. While it is set, NO preview/refresh/back/mode change may replace the number: the
  // job-ref step shows it read-only and loadJobRefPreview() never asks the database for another one.
  // The reservation is retired by the database when the job row is inserted (trigger on jobs).
  const [lockedReservation] = useState<{ id: string; job_ref: string } | null>(
    (location.state as { reservation?: { id: string; job_ref: string } } | null)?.reservation || null
  )

  const [step, setStep] = useState<Step>('mode')
  const [mode, setMode] = useState<ProcessingMode | null>(null)
  const [fileFormat, setFileFormat] = useState<'pdf' | 'xlsx' | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [mblFile, setMblFile] = useState<File | null>(null)
  const [hblFile, setHblFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [progress, setProgress] = useState('')
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null)
  const [editedData, setEditedData] = useState<ExtractedData | null>(null)
  const [masterRowData, setMasterRowData] = useState<ExtractedData | null>(null)
  const [detailRowsData, setDetailRowsData] = useState<ExtractedData[]>([])
  const [jobRef, setJobRef] = useState('')
  const [createdJobId, setCreatedJobId] = useState<string | null>(null)
  const [boxtraxRef, setBoxtraxRef] = useState<string | null>(null)
  const [boxtraxSyncStatus, setBoxtraxSyncStatus] = useState<'pending' | 'syncing' | 'success' | 'warning' | 'error'>('pending')
  // Z58.7: the automatic post-creation sync opens the consolidated Sync Preflight modal FIRST
  // (InvoiceTo + Shipper/Consignee/Notify, all with a visible status) instead of firing the sync
  // straight away and reacting to whatever it blocks on -- that old flow had no way to show a
  // party that never gets blocked at all (e.g. Consignee/Notify sharing InvoiceTo's own BoxTop
  // account).
  const [pendingPreflight, setPendingPreflight] = useState<{ jobId: string } | null>(null)
  const [boxtraxError, setBoxtraxError] = useState('')
  const [detectedHBLs, setDetectedHBLs] = useState<HBLData[]>([])
  const [selectedHBLsForCreation, setSelectedHBLsForCreation] = useState<Set<string>>(new Set())
  const [isParsing, setIsParsing] = useState(false)
  const [parseProgress, setParseProgress] = useState('')
  const [addressCheckModal, setAddressCheckModal] = useState(false)
  const [pendingCustomers, setPendingCustomers] = useState<Array<{ type: 'shipper' | 'consignee'; name: string; address?: string; matchedCustomers: any[] }>>([])
  const [customerAction, setCustomerAction] = useState<Record<string, 'create' | 'select' | 'skip'>>({})
  const [selectedCustomers, setSelectedCustomers] = useState<Record<string, string>>({})

  // Phase 2: Address validation & Job creation states
  const [addressDecisions, setAddressDecisions] = useState<any[]>([])
  const [customerMappings, setCustomerMappings] = useState<any[]>([])
  const [plannedParentJobRef, setPlannedParentJobRef] = useState<string | null>(null)
  const [showJobConfirmDialog, setShowJobConfirmDialog] = useState(false)
  const [jobCreationLoading, setJobCreationLoading] = useState(false)
  const [createdParentJobRef, setCreatedParentJobRef] = useState<string | null>(null)
  const [createdParentJobId, setCreatedParentJobId] = useState<string | null>(null)
  const [createdChildJobIds, setCreatedChildJobIds] = useState<string[]>([])
  const [createdChildJobRefs, setCreatedChildJobRefs] = useState<string[]>([])
  const [createdCustomers, setCreatedCustomers] = useState<any[]>([])

  // Job reference preview states
  const [previewJobRef, setPreviewJobRef] = useState<string | null>(null)
  const [userJobRef, setUserJobRef] = useState<string | null>(null)
  const [jobRefError, setJobRefError] = useState<string | null>(null)
  // Z31/Z36: soft, non-blocking -- distinct from jobRefError (format/duplicate-job, which
  // blocks Continue). Set when the ref matches an ACTIVE job_ref_reservations row held by
  // someone else. dismissedWarningRef tracks a ref the user explicitly clicked "Use anyway"
  // on, so the banner doesn't reappear for that same value (it does reappear if they edit to a
  // different reserved ref).
  const [jobRefWarning, setJobRefWarning] = useState<{ ref: string; reservedBy: string; reservedAt: string; note: string | null } | null>(null)
  const [dismissedWarningRef, setDismissedWarningRef] = useState<string | null>(null)
  const [loadingJobRef, setLoadingJobRef] = useState(false)

  // Google Drive integration states
  const [dataSource, setDataSource] = useState<DataSource>('drive')
  const [showDataSourcePicker, setShowDataSourcePicker] = useState(true)
  const [driveToken, setDriveToken] = useState<string | null>(null)
  const [driveFolderUrl, setDriveFolderUrl] = useState('')
  const [driveFolderFiles, setDriveFolderFiles] = useState<DriveFileItem[]>([])
  const [driveJobGroups, setDriveJobGroups] = useState<DriveJobGroup[]>([])
  const [driveScanLoading, setDriveScanLoading] = useState(false)
  const [showAllFileTypes, setShowAllFileTypes] = useState(false)
  const [currentDriveGroupIndex, setCurrentDriveGroupIndex] = useState(0)

  // #291/#289: Container type + Freight term option lists, fed into MBLHBLTable's own
  // per-row/per-master-row dropdown cells (#12/#14: the old shared "apply to all" dropdowns
  // that lived here were replaced by in-table fill-down/push-down controls).
  const [containerTypes, setContainerTypes] = useState<{ id: string; code: string; boxtop_name: string | null }[]>([])
  const [shippingTerms, setShippingTerms] = useState<{ id: string; code: string; description: string | null }[]>([])
  // Task J: Shipping Line dropdown on the pre-fill form
  const [shippingLines, setShippingLines] = useState<{ id: string; code: string | null; name: string }[]>([])
  // Y2: Departure/Arrival Port "Name (CODE)" auto-fill, Created By dropdown, Shed dropdown
  const [ports, setPorts] = useState<{ id: string; code: string; name: string; type: string; un_locode: string | null }[]>([])
  const [autoJobUsers, setAutoJobUsers] = useState<string[]>([])
  const [sheds, setSheds] = useState<{ id: string; code: string; name: string | null }[]>([])
  // Y-followup (task 22, bug F): Invoice To / Overseas Agent / Port Agent dropdowns
  const [customerOptions, setCustomerOptions] = useState<{ id: string; code: string; name: string }[]>([])

  const editData = editedData || extractedData

  // Load container type / freight term reference data for the review-step dropdowns
  useEffect(() => {
    supabase.from('container_types').select('id, code, boxtop_name').eq('is_active', true).order('display_order')
      .then(({ data }) => setContainerTypes(data || []))
    supabase.from('shipping_terms').select('id, code, description').eq('is_active', true).order('code')
      .then(({ data }) => setShippingTerms(data || []))
    // Z41 hotfix: was .eq('is_active', true), which silently drops any row where is_active is
    // NULL rather than an explicit false (legacy rows predating that column, or ones the Data
    // admin pages never touched) -- the Ports/Carriers admin pages' default view has no active
    // filter at all, so a row like this shows up there but vanished from every dropdown fed by
    // this query. .not(...'is', false) keeps true and NULL, only excludes an explicit false.
    //
    // Z41.5: neither query had a .limit()/.range(), so both relied on PostgREST's implicit
    // default row cap (1000). ports has 41k+ active rows sorted alphabetically by name, so the
    // client-side match list silently cut off after "Alz..." -- Felixstowe, Yantian, Nansha,
    // Shanghai etc. were never in the array the Confirm dialog matched against, so they always
    // showed "Will create new" even though the row existed. fetchAllRows pages through with
    // .range() until a short page confirms there's no more, so the full table always loads.
    fetchAllRows(() =>
      supabase.from('carriers').select('id, code, name').eq('type', 'SHIPPING_LINE').not('is_active', 'is', false).order('name')
    )
      .then(({ data, error }) => {
        if (error) console.error('[AutoJobsPage] Failed to load carriers for Confirm dialog:', error)
        else if (data.length === 0) console.warn('[AutoJobsPage] Carriers query returned 0 rows -- Carrier dropdown will be empty')
        setShippingLines(data)
      })
    fetchAllRows(() =>
      supabase.from('ports').select('id, code, name, type, un_locode').not('is_active', 'is', false).order('name')
    )
      .then(({ data, error }) => {
        if (error) console.error('[AutoJobsPage] Failed to load ports for Confirm dialog:', error)
        else if (data.length === 0) console.warn('[AutoJobsPage] Ports query returned 0 rows -- Port dropdowns will show no matches')
        setPorts(data)
      })
    supabase.from('user_roles').select('user_email').eq('is_active', true).order('user_email')
      .then(({ data }) => setAutoJobUsers((data || []).map(u => u.user_email)))
    supabase.from('sheds').select('id, code, name').eq('is_active', true).order('code')
      .then(({ data }) => setSheds(data || []))
    // Z88: paged (customers is in the thousands; an unpaged read silently stops at 1000).
    fetchAllRows<{ id: string; code: string; name: string }>(() =>
      supabase.from('customers').select('id, code, name').eq('is_active', true).order('name').order('id')
    ).then(({ data, error }) => {
      if (error) console.error('Error loading customers:', error)
      setCustomerOptions(data)
    })
  }, [])

  // OAuth token handling
  useEffect(() => {
    // Try to restore token from sessionStorage
    const saved = getAutoJobsDriveToken()
    if (saved) {
      setDriveToken(saved)
      return
    }

    // Check for OAuth callback in URL hash
    const hash = window.location.hash
    if (hash.includes('access_token=')) {
      const token = parseTokenFromHash()
      if (token) {
        saveAutoJobsDriveToken(token)
        setDriveToken(token)

        // Z62: another page (e.g. Three-Way Verification) can reuse this same Drive OAuth
        // flow -- the redirect_uri is fixed to this page (already registered with Google), so
        // a caller elsewhere sets this marker before redirecting here, and we bounce it back
        // once the token is captured. The token itself stays in the shared sessionStorage key,
        // so the other page picks it up immediately on mount -- no second auth round-trip.
        const externalReturn = sessionStorage.getItem('drive_oauth_external_return')
        if (externalReturn) {
          sessionStorage.removeItem('drive_oauth_external_return')
          window.history.replaceState(null, '', window.location.pathname)
          navigate(externalReturn)
          return
        }

        // Try to restore saved OAuth state (mode, URL, step)
        const oauthState = sessionStorage.getItem('autojobs_oauth_state')
        const hadOAuthError = sessionStorage.getItem('autojobs_oauth_error') === '401'
        if (oauthState) {
          try {
            const state = JSON.parse(oauthState)
            if (state.mode && state.driveUrl) {
              setMode(state.mode)
              setDataSource('drive')
              setDriveFolderUrl(state.driveUrl)
              sessionStorage.removeItem('autojobs_oauth_state')
              sessionStorage.removeItem('autojobs_oauth_error')

              // Clear URL hash to clean up
              window.history.replaceState(null, '', window.location.pathname)

              // If we had a 401 error, show success message
              if (hadOAuthError) {
                setError('Google Drive session re-authorized successfully. Resuming...')
              }

              // Set flag to auto-scan folder after state update
              sessionStorage.setItem('autojobs_auto_scan', '1')
              return
            }
          } catch (err) {
            console.log('[AutoJobsPage] Failed to restore OAuth state:', err)
            sessionStorage.removeItem('autojobs_oauth_state')
            sessionStorage.removeItem('autojobs_oauth_error')
          }
        }

        // Fallback: just go to drive_input step
        setDataSource('drive')
        setStep('drive_input')
        window.history.replaceState(null, '', window.location.pathname)
      }
    }
  }, [])

  // Reset data source picker when step changes
  useEffect(() => {
    if (step !== 'upload') {
      setShowDataSourcePicker(true)
    }
  }, [step])

  // Auto-scan folder after OAuth redirect restore
  useEffect(() => {
    if (sessionStorage.getItem('autojobs_auto_scan') === '1' && driveFolderUrl && driveToken) {
      sessionStorage.removeItem('autojobs_auto_scan')
      handleScanDriveFolder()
    }
  }, [driveFolderUrl, driveToken])

  // Note: Parent job ref is now generated by Edge Function only
  // This eliminates sequence consumption and ref mismatch issues
  // Frontend only shows "will be assigned upon creation"

  // ---- BROWSER-SIDE PARSING: isHBLSheetName, parseHBLExcel, getCellValue, isAirManifestExcel, parseAirManifestExcel, parseInvoiceBoxTemplate, isChineseInvoiceExcel,
  // parseChineseInvoiceExcel, parseMultiHBLPDF, detectTemplateByKeywords: ENTRY REMOVED 2026-11-05 (manifest, bundled, invoices), KEPT FOR REFERENCE. ----
  // They are reached only from the bundled / manifest / invoices branches of handleProcess, which nothing can open any more. They are NOT deleted: a proper
  // unification of the browser and server parse paths needs them as the reference for what the server cannot read yet (the invoice-box and Chinese-invoice Excel
  // layouts, the 'every sheet name is an HBL reference' rule of the HBL workbook, the template detection against document_templates).
  // Known disagreements with the server (parse-document), the reason this entry was closed: parseAirManifestExcel starts its rows at row 6 and reads column I as isLocal
  // (the server: row 7, column I = IMPORTER / EORI); parseHBLExcel returns whole cells (name + address + EORI in one string) where the server splits them; the invoice parsers
  // read HS-coded lines the server's HBL prompt does not. Do not 'fix' these in place: settle the single parse path first (docs/go_live_checklist.md section 8).
  function isHBLSheetName(name: string): boolean {
    // Check if sheet name looks like HBL ref: letters+numbers, 7-10 chars
    return /^[A-Z0-9]{7,10}$/.test(name)
  }

  function parseHBLExcel(file: File): Promise<HBLData[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const data = e.target?.result
          const workbook = XLSX.read(data, { type: 'array' })
          const hbls: HBLData[] = []

          // Check if ALL sheets are HBL references (multi-HBL mode)
          const allAreHBLRefs = workbook.SheetNames.every(name => isHBLSheetName(name))
          if (!allAreHBLRefs && workbook.SheetNames.length > 1) {
            reject(new Error('Not all sheets have HBL reference names'))
            return
          }

          // Parse each sheet
          workbook.SheetNames.forEach((sheetName, sheetIndex) => {
            const worksheet = workbook.Sheets[sheetName]

            // Skip sheets that don't look like HBL refs
            if (!isHBLSheetName(sheetName)) return

            // Log raw cell values for debugging (first sheet only)
            if (sheetIndex === 0) {
              console.log(`[AutoJobsPage] Parsing first sheet: ${sheetName}`)
              console.log('[AutoJobsPage] Raw cell values (corrected coordinates):')
              console.log('  B3 (Shipper):', getCellValue(worksheet, 'B3'))
              console.log('  B7 (Consignee):', getCellValue(worksheet, 'B7'))
              console.log('  B14 (Vessel):', getCellValue(worksheet, 'B14'))
              console.log('  C14 (Place of receipt):', getCellValue(worksheet, 'C14'))
              console.log('  B16 (POD):', getCellValue(worksheet, 'B16'))
              console.log('  B18 (Marks/Nos):', getCellValue(worksheet, 'B18'))
              console.log('  C18 (Packages):', getCellValue(worksheet, 'C18'))
              console.log('  D18 (Cargo):', getCellValue(worksheet, 'D18'))
              console.log('  J18 (Gross Weight):', getCellValue(worksheet, 'J18'))
              console.log('  K18 (Volume CBM):', getCellValue(worksheet, 'K18'))
              console.log('  B24 (Container/Seal):', getCellValue(worksheet, 'B24'))
            }

            // Extract BL number from sheet name or first row if available
            let blNumber = sheetName

            // Fixed cell positions for HBL data extraction (corrected based on actual Excel structure)
            const shipper = getCellValue(worksheet, 'B3').trim()
            const consigneeCell = getCellValue(worksheet, 'B7').trim()

            // Extract EORI number from consignee cell (may contain EORI code)
            let consigneeEori: string | undefined
            if (consigneeCell.includes('EORI:')) {
              consigneeEori = consigneeCell.split('EORI:')[1]?.trim()
            } else {
              const eoriMatch = consigneeCell.match(/(GB\d{12,15}|[A-Z]{2}\d{10,15})/)
              if (eoriMatch) {
                consigneeEori = eoriMatch[0]
              }
            }

            // Parse packages field which may contain unit (e.g., "26CARTONS")
            const packagesStr = getCellValue(worksheet, 'C18').trim()
            const packagesMatch = packagesStr.match(/\d+/)
            const packagesNum = packagesMatch ? parseInt(packagesMatch[0]) : undefined

            // Parse weight and volume fields
            const grossWeightStr = getCellValue(worksheet, 'J18').trim()
            const grossWeightKgs = grossWeightStr ? parseFloat(grossWeightStr) : undefined

            const volumeStr = getCellValue(worksheet, 'K18').trim()
            const volumeCbm = volumeStr ? parseFloat(volumeStr) : undefined

            // Parse container info from B24 (format: "CSNU6621089/40HQ/SYA4919471")
            const containerRawStr = getCellValue(worksheet, 'B24').trim()
            const containerParts = containerRawStr.split('/').map(s => s.trim())
            const containerNumber = containerParts[0] || undefined
            const containerType = containerParts[1] || undefined
            const sealNo = containerParts[2] || undefined

            const hblData: HBLData = {
              sheetName: sheetName,
              hblNumber: blNumber,
              shipper: shipper,
              consignee: consigneeCell,
              consigneeEori: consigneeEori,
              vessel: getCellValue(worksheet, 'B14').trim(),
              ports: {
                loading: getCellValue(worksheet, 'C14').trim(),
                discharge: getCellValue(worksheet, 'B16').trim(),
                finalDestination: undefined
              },
              packages: packagesStr,
              pieces: packagesNum,
              cargoDescription: getCellValue(worksheet, 'D18').trim(),
              containerNumber: containerNumber,
              sealNo: sealNo,
              containerType: containerType,
              grossWeightKgs: grossWeightKgs,
              volumeCbm: volumeCbm,
            }

            if (sheetIndex === 0) {
              console.log('[AutoJobsPage] Extracted data for first sheet:', hblData)
            }

            hbls.push(hblData)
          })

          if (hbls.length === 0) {
            reject(new Error('No HBL sheets found'))
            return
          }

          resolve(hbls)
        } catch (err) {
          reject(err instanceof Error ? err : new Error('Failed to parse Excel file'))
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsArrayBuffer(file)
    })
  }

  function getCellValue(worksheet: XLSX.WorkSheet, cellRef: string): string {
    const cell = worksheet[cellRef]
    if (!cell) return ''
    return cell.v?.toString() || ''
  }

  async function extractTextFromPDF(file: File): Promise<string> {
    // Convert PDF to base64 and send to server for extraction
    const arrayBuffer = await file.arrayBuffer()
    const bytes = new Uint8Array(arrayBuffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    const base64 = btoa(binary)

    // Call server endpoint to extract text from PDF
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    if (!supabaseUrl || !anonKey) {
      throw new Error('Missing Supabase configuration')
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/extract-pdf-text`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ pdfBase64: base64 })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to extract PDF text')
    }

    const data = await response.json()
    return data.text || ''
  }

  async function extractTextFromExcel(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target?.result
          const workbook = XLSX.read(data, { type: 'array' })
          let text = ''
          workbook.SheetNames.forEach(sheetName => {
            const worksheet = workbook.Sheets[sheetName]
            const csv = XLSX.utils.sheet_to_csv(worksheet)
            text += csv + '\n'
          })
          resolve(text)
        } catch (err) {
          reject(err)
        }
      }
      reader.readAsArrayBuffer(file)
    })
  }

  async function extractTextFromExcelForTemplateDetection(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target?.result
          const workbook = XLSX.read(data, { type: 'array' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]

          if (!worksheet) {
            resolve('')
            return
          }

          let text = ''
          // Extract text from first 30 rows, all columns
          for (let rowIdx = 0; rowIdx < 30; rowIdx++) {
            for (let colIdx = 0; colIdx < 26; colIdx++) { // A-Z columns
              const cellRef = XLSX.utils.encode_col(colIdx) + XLSX.utils.encode_row(rowIdx)
              const cellValue = getCellValue(worksheet, cellRef)
              if (cellValue) {
                text += cellValue + ' '
              }
            }
          }

          resolve(text)
        } catch (err) {
          reject(err)
        }
      }
      reader.readAsArrayBuffer(file)
    })
  }

  async function parseMultiHBLPDF(file: File): Promise<HBLData[]> {
    // Convert PDF to base64
    const arrayBuffer = await file.arrayBuffer()
    const bytes = new Uint8Array(arrayBuffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    const base64 = btoa(binary)

    // Call parseDocumentAuto with manifest mode to extract all HBLs
    try {
      const result = await parseDocumentAuto(base64, 'application/pdf', 'manifest')

      if (!result) {
        throw new Error('Failed to extract HBL data')
      }

      // Parse the result to extract multiple HBLs
      // The result contains the extracted data which we'll format as HBLData
      const hbls: HBLData[] = []

      // Try to parse the summary to get HBL array (if it contains JSON)
      let hblArray: any[] = []
      // Extract summary from either flat structure or master/details structure
      const summary = (result as any).summary || (result as any).master?.summary || (result as any).details?.[0]?.summary
      if (summary && typeof summary === 'string' && summary.includes('{')) {
        try {
          const jsonMatch = summary.match(/\{[\s\S]*\}/)
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0])
            hblArray = parsed.hbls || [parsed]
          }
        } catch (e) {
          // If JSON parsing fails, create a single HBL from the result
          hblArray = [result]
        }
      } else {
        hblArray = [result]
      }

      // Convert extracted data to HBLData format
      hblArray.forEach((hbl: any, index: number) => {
        hbls.push({
          sheetName: `Page ${index + 1}`,
          pageNumber: index + 1,
          hblNumber: hbl.bl_number || hbl.mbl_number || hbl.waybill_number,
          shipper: hbl.shipper?.name || hbl.shipper,
          consignee: hbl.consignee?.name || hbl.consignee,
          consigneeEori: hbl.consignee_eori,
          consigneeAddress: hbl.consignee?.address || hbl.consignee_address,
          vessel: hbl.vessel_name || hbl.vessel || hbl.flight_vessel,
          ports: {
            loading: hbl.ports?.loading || hbl.port_of_loading,
            discharge: hbl.ports?.discharge || hbl.port_of_discharge,
            finalDestination: hbl.ports?.delivery
          },
          cargoDescription: hbl.cargo?.description || hbl.cargo_description,
          packages: hbl.container?.packages || hbl.packages,
          containerNumber: hbl.container?.number || hbl.container_number,
          grossWeightKgs: hbl.cargo?.gross_weight_kgs || hbl.gross_weight_kgs,
          volumeCbm: hbl.cargo?.volume_cbm || hbl.volume_cbm,
          onBoardDate: hbl.on_board_date
        })
      })

      if (hbls.length === 0) {
        throw new Error('No HBLs found in PDF')
      }

      return hbls
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to parse multi-HBL PDF'
      throw new Error(errMsg)
    }
  }

  function isAirManifestExcel(worksheet: XLSX.WorkSheet): boolean {
    // Check if this is an air manifest by looking for MAWB NO or AIR CARGO MANIFEST text
    const cell00 = getCellValue(worksheet, 'A1') || ''
    const cell01 = getCellValue(worksheet, 'B1') || ''
    const cell02 = getCellValue(worksheet, 'C1') || ''
    const row4 = getCellValue(worksheet, 'A5') || ''

    return (
      cell00.includes('MAWB') ||
      cell01.includes('MAWB') ||
      cell02.includes('MAWB') ||
      row4.includes('AIR CARGO MANIFEST') ||
      row4.includes('HAWB')
    )
  }

  function parseAirManifestExcel(file: File): Promise<HBLData[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target?.result
          const workbook = XLSX.read(data, { type: 'array' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]

          if (!isAirManifestExcel(worksheet)) {
            reject(new Error('Not an air manifest Excel file'))
            return
          }

          // Parse header information
          const mawbNumber = getCellValue(worksheet, 'B1').trim()
          const origin = getCellValue(worksheet, 'E1').trim()

          // Find destination (last column with data in row 1)
          let destination = ''
          for (let col = 15; col > 5; col--) {
            const val = getCellValue(worksheet, XLSX.utils.encode_col(col) + '1')
            if (val.trim()) {
              destination = val.trim()
              break
            }
          }

          const flightDate = getCellValue(worksheet, 'B2').trim()

          // Parse HAWB rows starting from row 6 (index 5)
          const hawbs: HBLData[] = []
          let rowIndex = 5
          const maxRows = 1000

          while (rowIndex < maxRows) {
            const hawbNo = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'A').trim()

            if (!hawbNo) {
              // Empty HAWB NO indicates end of list or totals row
              break
            }

            const packagesStr = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'B').trim()
            const weightStr = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'C').trim()
            const volumeStr = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'D').trim()
            const description = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'E').trim()
            const shipperName = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'F').trim()
            const consigneeName = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'G').trim()
            const pvaStr = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'H').trim()
            const isLocalStr = getCellValue(worksheet, XLSX.utils.encode_row(rowIndex) + 'I').trim()

            // Extract numeric values
            const packagesMatch = packagesStr.match(/\d+/)
            const packages = packagesMatch ? packagesMatch[0] : packagesStr

            const grossWeightKgs = parseFloat(weightStr) || undefined
            const volumeCbm = parseFloat(volumeStr) || undefined
            const pva = pvaStr.toUpperCase().includes('PVA')
            const isLocal = isLocalStr ? (isLocalStr.toUpperCase().includes('YES') || isLocalStr === '1') : undefined

            hawbs.push({
              sheetName: `HAWB ${hawbNo}`,
              hawbNumber: hawbNo,
              shipper: shipperName || undefined,
              consignee: consigneeName || undefined,
              cargoDescription: description || undefined,
              packages: packages,
              grossWeightKgs: grossWeightKgs,
              volumeCbm: volumeCbm,
              pva: pva,
              isLocal: isLocal,
              mawbNumber: mawbNumber,
              origin: origin,
              destination: destination,
              flightDate: flightDate
            })

            rowIndex++
          }

          if (hawbs.length === 0) {
            reject(new Error('No HAWB entries found in manifest'))
            return
          }

          resolve(hawbs)
        } catch (err) {
          reject(err instanceof Error ? err : new Error('Failed to parse air manifest'))
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsArrayBuffer(file)
    })
  }

  function parseInvoiceBoxTemplate(file: File): Promise<ExtractedData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target?.result
          const workbook = XLSX.read(data, { type: 'array' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]

          // Extract fields from fixed positions (0-based row/column indexing)
          const hblNumber = getCellValue(worksheet, XLSX.utils.encode_col(3) + XLSX.utils.encode_row(1)).trim() // rows[1][3]
          const clientRef = getCellValue(worksheet, XLSX.utils.encode_col(1) + XLSX.utils.encode_row(1)).trim() // rows[1][1]
          const shipper = getCellValue(worksheet, XLSX.utils.encode_col(1) + XLSX.utils.encode_row(13)).trim() // rows[13][1]
          const consignee = getCellValue(worksheet, XLSX.utils.encode_col(1) + XLSX.utils.encode_row(18)).trim() // rows[18][1]
          const eori = getCellValue(worksheet, XLSX.utils.encode_col(1) + XLSX.utils.encode_row(23)).trim() // rows[23][1]
          const vat = getCellValue(worksheet, XLSX.utils.encode_col(1) + XLSX.utils.encode_row(22)).trim() // rows[22][1]
          const invoiceNo = getCellValue(worksheet, XLSX.utils.encode_col(1) + XLSX.utils.encode_row(27)).trim() // rows[27][1]

          // Extract line items from row 29+ where column 1 is a number (HS code)
          const lineItems = []
          let rowIdx = 29
          const maxRows = 100

          while (rowIdx < maxRows) {
            const hsCodeCell = XLSX.utils.encode_col(1) + XLSX.utils.encode_row(rowIdx)
            const hsCode = getCellValue(worksheet, hsCodeCell).trim()

            if (!hsCode || isNaN(Number(hsCode))) break // Stop when HS code is empty or not a number

            const descCell = XLSX.utils.encode_col(2) + XLSX.utils.encode_row(rowIdx)
            const description = getCellValue(worksheet, descCell).trim()

            lineItems.push({
              hs_code: hsCode,
              goods_description: description
            })

            rowIdx++
          }

          console.log('[AutoJobsPage] Parsed 发票箱单 format:', { hblNumber, clientRef, shipper, consignee, eori, vat, invoiceNo, itemCount: lineItems.length })

          const result: ExtractedData = {
            transport_mode: 'SEA',
            direction: 'IMP',
            mbl_number: hblNumber || undefined,
            waybill_number: invoiceNo || undefined,
            shipper: { name: shipper || undefined },
            // This sheet has the consignee's EORI (row 24) and VAT number (row 23) in their own cells. They used to go into `contact` (read downstream as a
            // PHONE number: EORI numbers ended up in customer_addresses.contact_phone) and the VAT was read and then dropped.
            consignee: {
              name: consignee || undefined,
              eori: eori || undefined,
              vat_no: vat || undefined
            },
            container: {
              packages: lineItems.length.toString()
            },
            cargo: {
              description: lineItems.map(item => `${item.hs_code}: ${item.goods_description}`).join('; ') || undefined,
              pieces: lineItems.length
            },
            summary: `Invoice #${invoiceNo} - ${lineItems.length} items, Ref: ${clientRef || 'N/A'}`
          }

          resolve(result)
        } catch (err) {
          reject(err instanceof Error ? err : new Error('Failed to parse 发票箱单 format'))
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsArrayBuffer(file)
    })
  }

  function isChineseInvoiceExcel(worksheet: XLSX.WorkSheet): boolean {
    const cell00 = getCellValue(worksheet, 'A1')
    const hasSeaFreight = cell00.includes('海运分单')

    // Also check row 28 for HS CODE marker
    const row29A = getCellValue(worksheet, 'A29')
    const row29B = getCellValue(worksheet, 'B29')
    const hasHSCodeMarker = row29A.includes('HS CODE') || row29B.includes('HS CODE') ||
                            row29A.includes('S/N') || row29B.includes('S/N')

    return hasSeaFreight && hasHSCodeMarker
  }

  function parseChineseInvoiceExcel(file: File): Promise<ExtractedData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target?.result
          const workbook = XLSX.read(data, { type: 'array' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]

          if (!isChineseInvoiceExcel(worksheet)) {
            reject(new Error('Not a Chinese customs invoice format'))
            return
          }

          // Log raw values for debugging (first parse only)
          console.log('[AutoJobsPage] Parsing Chinese customs invoice')
          console.log('  A1 (format check):', getCellValue(worksheet, 'A1'))
          console.log('  B2 (client_ref):', getCellValue(worksheet, 'B2'))
          console.log('  D2 (hbl_number):', getCellValue(worksheet, 'D2'))
          console.log('  B4 (currency):', getCellValue(worksheet, 'B4'))
          console.log('  D4 (vat_deferred):', getCellValue(worksheet, 'D4'))

          // Extract header fields
          const clientRef = getCellValue(worksheet, 'B2').trim() // 客户参考号
          const hblNumber = getCellValue(worksheet, 'D2').trim() // 分单号
          // const currency = getCellValue(worksheet, 'B4').trim()
          // const vatDeferred = getCellValue(worksheet, 'D4').trim() === '是'

          const shipper = getCellValue(worksheet, 'B14').trim() // 出口商名称 (row 14 = row index 13)
          const consignee = getCellValue(worksheet, 'B19').trim() // 进口商名称 (row 19 = row index 18)
          const consigneeAddress = getCellValue(worksheet, 'B20').trim() // 进口商地址 (row 20 = row index 19)
          const consigneeEori = getCellValue(worksheet, 'B24').trim() // EORI No. (row 24 = row index 23)
          // const consigneeVat = getCellValue(worksheet, 'B23').trim() // VAT No. (row 23 = row index 22)
          const invoiceNo = getCellValue(worksheet, 'B28').trim() // row 28 = row index 27

          // Extract line items from row 29+ (0-based), which is row 30+ in Excel (1-based)
          const lineItems = []
          let rowIdx = 29 // 0-based row 29 = Excel row 30
          const maxRows = 100

          while (rowIdx < maxRows) {
            const cellRef = XLSX.utils.encode_col(0) + XLSX.utils.encode_row(rowIdx)
            const hsCode = getCellValue(worksheet, cellRef).trim()

            if (!hsCode) break // Empty HS code indicates end of items

            const descCell = XLSX.utils.encode_col(1) + XLSX.utils.encode_row(rowIdx)
            const ctnsCell = XLSX.utils.encode_col(2) + XLSX.utils.encode_row(rowIdx)
            const pcsCell = XLSX.utils.encode_col(3) + XLSX.utils.encode_row(rowIdx)

            const description = getCellValue(worksheet, descCell).trim()
            const ctns = getCellValue(worksheet, ctnsCell).trim()
            const pcs = getCellValue(worksheet, pcsCell).trim()

            lineItems.push({
              hs_code: hsCode,
              goods_description: description,
              ctns: ctns,
              pcs: pcs
            })

            rowIdx++
          }

          if (lineItems.length === 0) {
            reject(new Error('No line items found in Chinese invoice'))
            return
          }

          // Map to ExtractedData format
          const result: ExtractedData = {
            transport_mode: 'SEA',
            direction: 'IMP',
            doc_type: 'INVOICE',
            mbl_number: hblNumber || undefined,
            waybill_number: invoiceNo || undefined,
            shipper: { name: shipper || undefined },
            consignee: {
              name: consignee || undefined,
              address: consigneeAddress || undefined,
              contact: consigneeEori || undefined
            },
            container: {
              packages: lineItems.length.toString()
            },
            cargo: {
              description: lineItems.map(item => `${item.hs_code}: ${item.goods_description}`).join('; ') || undefined,
              pieces: lineItems.length
            },
            summary: `Chinese Invoice #${invoiceNo} - ${lineItems.length} items, Ref: ${clientRef || 'N/A'}`
          }

          console.log('[AutoJobsPage] Extracted Chinese invoice data:', result)
          resolve(result)
        } catch (err) {
          reject(err instanceof Error ? err : new Error('Failed to parse Chinese invoice'))
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsArrayBuffer(file)
    })
  }

  // Z58.24: delegates the actual matching to matchTemplateByCustomerOrKeywords
  // (templateManager.ts) so a template linked to the customer named in the
  // document text is preferred over one that only matches on keyword overlap —
  // the original "2 customers, similar layout" mis-match problem this ticket
  // was raised for. Keeps this function's own (fetch + match) shape so all 5
  // existing call sites are unchanged.
  async function detectTemplateByKeywords(fileText: string): Promise<DocumentTemplate | null> {
    try {
      const { data: templates, error } = await supabase
        .from('document_templates')
        .select('*')

      if (error) {
        console.error('Failed to fetch templates:', error)
        return null
      }

      return matchTemplateByCustomerOrKeywords(templates || [], fileText)
    } catch (err) {
      console.error('Error detecting template:', err)
      return null
    }
  }

  function getFileAccept() {
    if (fileFormat === 'pdf') return '.pdf'
    if (fileFormat === 'xlsx') return '.xlsx,.xls'
    return '.pdf,.xlsx,.xls'
  }

  function getFormatDescription() {
    if (fileFormat === 'pdf') return 'PDF files'
    if (fileFormat === 'xlsx') return 'Excel spreadsheet files'
    return 'All supported formats'
  }

  // Z31: warns (doesn't block) when `ref` matches an ACTIVE pre-reservation held by someone
  // other than the current user. generate_job_ref/preview_next_job_ref already skip active
  // reservations in their MAX calc, so the auto-suggested ref should never hit this in
  // practice -- this mainly guards a manually-typed ref colliding with someone else's hold.
  async function checkReservationWarning(ref: string) {
    try {
      const { data } = await supabase
        .from('job_ref_reservations')
        .select('reserved_by, note, reserved_at')
        .eq('job_ref', ref)
        .eq('status', 'active')
        .maybeSingle()

      if (data && data.reserved_by !== (userEmail || '')) {
        setJobRefWarning({ ref, reservedBy: data.reserved_by, reservedAt: data.reserved_at, note: data.note })
      } else {
        setJobRefWarning(null)
      }
    } catch (err) {
      console.error('[AutoJobsPage] Error checking job ref reservation:', err)
    }
  }

  // Z36: reset to the system's own suggestion, which by construction (generate_job_ref excludes
  // active reservations from its MAX calc) is never itself actively reserved.
  function handleSkipToAvailable() {
    if (previewJobRef) handleJobRefChange(previewJobRef)
  }

  // Z36: dismiss the banner for this exact ref -- only stops nagging about a ref the user has explicitly chosen to proceed with.
  function handleUseAnyway() {
    if (jobRefWarning) setDismissedWarningRef(jobRefWarning.ref)
  }

  // Job reference preview functions
  async function loadJobRefPreview(direction: string = 'IMP') {
    if (lockedReservation) {
      // Locked number: re-check it is still ours to use, never fetch a different one.
      try {
        setLoadingJobRef(true)
        setJobRefWarning(null)
        setPreviewJobRef(lockedReservation.job_ref)
        setUserJobRef(lockedReservation.job_ref)
        const { data, error } = await supabase.from('job_ref_reservations').select('status').eq('id', lockedReservation.id).maybeSingle()
        if (error || !data) setJobRefError('The reservation could not be found. Go back to Jobs and use it again.')
        else if (data.status !== 'active') setJobRefError(`${lockedReservation.job_ref} is no longer reserved (status: ${data.status}). Go back to Jobs and reserve a number again.`)
        else setJobRefError(null)
      } finally {
        setLoadingJobRef(false)
      }
      return
    }
    try {
      setLoadingJobRef(true)
      setJobRefError(null)
      setJobRefWarning(null)

      const { data, error } = await supabase.rpc('preview_next_job_ref', {
        p_direction: direction,
      })

      if (error) {
        throw new Error(error.message)
      }

      setPreviewJobRef(data)
      setUserJobRef(data)
      console.log('[AutoJobsPage] Job ref preview:', data)

      if (data) checkReservationWarning(data)
    } catch (err) {
      console.error('[AutoJobsPage] Error loading job ref preview:', err)
      setJobRefError(err instanceof Error ? err.message : 'Failed to load job reference')
    } finally {
      setLoadingJobRef(false)
    }
  }

  function validateJobRef(ref: string): boolean {
    if (!ref) return false
    // Format: KI/KE + 5 digits (e.g., KI26001)
    const pattern = /^(KI|KE)\d{5}$/
    return pattern.test(ref)
  }

  async function handleJobRefChange(newRef: string) {
    if (lockedReservation) return
    const upperRef = newRef.toUpperCase()
    setUserJobRef(upperRef)
    setDismissedWarningRef(null)

    // Validate format
    if (!newRef) {
      setJobRefError(null)
      return
    }

    if (!validateJobRef(upperRef)) {
      setJobRefError('Format: KI/KE + 5 digits (e.g., KI26001)')
      return
    }

    // Check if job ref already exists in database
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('job_ref, created_at')
        .eq('job_ref', upperRef)
        .maybeSingle()

      if (error) {
        console.error('Error checking job ref:', error)
        setJobRefError('Failed to check availability')
        return
      }

      if (data) {
        const createdDate = new Date(data.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        setJobRefError(`⚠️ Job ref "${upperRef}" already exists (created ${createdDate})`)
        setJobRefWarning(null)
      } else {
        setJobRefError(null)
        checkReservationWarning(upperRef)
      }
    } catch (err) {
      console.error('Error querying database:', err)
      setJobRefError('Failed to check availability')
    }
  }

  // Z36 redo: the chosen number is only validated here. Nothing is written to job_ref_reservations
  // by this wizard any more -- a number is taken when the job row is inserted (create-autojobs uses
  // exactly this ref; the database retires a matching pre-reservation in the same transaction).
  async function applyUserJobRef() {
    if (!userJobRef || !validateJobRef(userJobRef)) {
      setJobRefError('Invalid job reference format')
      return false
    }
    return true
  }

  // Google Drive functions
  async function handleScanDriveFolder() {
    // Step 1: Validate folder URL
    const folderId = extractFolderIdFromUrl(driveFolderUrl)
    if (!folderId) {
      setError('Invalid Google Drive folder URL. Expected format: https://drive.google.com/drive/folders/FOLDER_ID')
      return
    }

    // Step 2: Check if we have OAuth token, if not trigger OAuth flow
    if (!driveToken) {
      setError('')
      // Save state before OAuth redirect so we can restore it after
      sessionStorage.setItem('autojobs_oauth_state', JSON.stringify({
        mode: mode,
        dataSource: 'drive',
        driveUrl: driveFolderUrl,
        step: 'drive_preview'
      }))
      initiateAutoJobsOAuth()
      return
    }

    // Step 3: Scan the folder
    setDriveScanLoading(true)
    setError('')
    try {
      const files = await listFolderFiles(folderId, driveToken)
      setDriveFolderFiles(files)
      setStep('drive_preview')
    } catch (err: any) {
      clearAutoJobsDriveToken()
      setDriveToken(null)

      if (err.message === 'UNAUTHORIZED') {
        // Token expired - trigger OAuth re-authorization flow
        sessionStorage.setItem('autojobs_oauth_state', JSON.stringify({
          mode: mode,
          dataSource: 'drive',
          driveUrl: driveFolderUrl,
          step: 'drive_preview'
        }))
        sessionStorage.setItem('autojobs_oauth_error', '401')
        initiateAutoJobsOAuth()
        return
      } else if (err.message === 'PERMISSION_DENIED') {
        setError('Permission denied. Please ensure the folder is shared with your Google account.')
      } else {
        setError(err.message || 'Failed to scan folder')
      }
    } finally {
      setDriveScanLoading(false)
    }
  }

  async function handleParseSelectedDriveFiles(selectedFileIds: Set<string>, fileRoles: Record<string, DriveFileWithRole['role']>) {
    if (selectedFileIds.size === 0 || !mode || !driveToken) return

    setError('')
    setProgress('Downloading and parsing files...')
    setIsParsing(true)

    try {
      // Get selected files with their assigned roles
      const selectedFiles = Array.from(selectedFileIds)
        .map(id => {
          const file = driveFolderFiles.find(f => f.id === id)
          return file ? { ...file, role: fileRoles[id] || 'hbl' } : null
        })
        .filter((f): f is DriveFileWithRole => f !== null)

      // Separate by role
      const mblFiles = selectedFiles.filter(f => f.role === 'mbl')

      // Validate required files
      if (mode === 'bundled' || mode === 'manifest' || mode === 'invoices' || mode === 'mbl_hbl') {
        if (mblFiles.length === 0) {
          setError('Please specify an MBL file (required for this mode)')
          setIsParsing(false)
          return
        }
      }

      // Download all selected files with their content
      setParseProgress(`Downloading ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''} from Drive...`)
      const filesWithContent = await Promise.all(
        selectedFiles.map(async file => {
          const content = await downloadFileAsBase64(file.id, driveToken)
          return {
            id: file.id,
            fileName: file.name,
            mimeType: file.mimeType,
            fileContent: content,
            role: file.role
          }
        })
      )

      // Separate by role
      const mblFilesWithContent = filesWithContent.filter(f => f.role === 'mbl')
      const otherFilesWithContent = filesWithContent.filter(f => f.role !== 'mbl')

      let parsed: any

      if (mode === 'single') {
        // Single mode - accept ANY file role (MBL, HBL, or other)
        if (filesWithContent.length === 0) {
          setError('No file selected')
          setIsParsing(false)
          return
        }

        const file = filesWithContent[0]
        parsed = await parseDocumentAuto(file.fileContent, file.mimeType, 'single')
        const singleData = parsed as unknown as ExtractedData
        setExtractedData(singleData)
      } else if (mode === 'mbl_hbl') {
        // MBL + HBL mode - requires BOTH
        const mblFile = mblFilesWithContent[0]
        if (!mblFile) {
          setError('Please specify an MBL file (required for MBL+HBL mode)')
          setIsParsing(false)
          return
        }

        if (otherFilesWithContent.length === 0) {
          setError('Please select at least one HBL file')
          setIsParsing(false)
          return
        }

        const hblFile = otherFilesWithContent[0]
        parsed = await parseDocumentAuto(
          {
            mblContent: mblFile.fileContent,
            hblContent: hblFile.fileContent,
            mblMimeType: mblFile.mimeType,
            hblMimeType: hblFile.mimeType
          },
          '',
          'mbl_hbl'
        )

        // Handle structured response
        if (parsed && typeof parsed === 'object' && 'master' in parsed && parsed.master) {
          const masterData = parsed.master as unknown as ExtractedData
          const detailsArray = (parsed.details || []) as unknown as ExtractedData[]
          setMasterRowData(masterData)
          setDetailRowsData(detailsArray)
          setExtractedData(masterData)
        } else {
          const singleData = parsed as unknown as ExtractedData
          setExtractedData(singleData)
        }
      } else if (mode === 'bundled' || mode === 'manifest' || mode === 'invoices') {
        // Bundled/Manifest/Invoices mode - requires MBL
        const mblFile = mblFilesWithContent[0]
        if (!mblFile) {
          setError('Please specify an MBL file (required for this mode)')
          setIsParsing(false)
          return
        }

        if (otherFilesWithContent.length === 0) {
          // Just MBL file
          parsed = await parseDocumentAuto(mblFile.fileContent, mblFile.mimeType, mode)
          const singleData = parsed as unknown as ExtractedData
          setExtractedData(singleData)
        } else {
          // MBL + HBL/Invoice/Manifest files — one HTTP call per HBL file (not
          // batches of 4): a single slow/multi-sheet HBL can no longer drag
          // 3 others over the 150s idle timeout with it. The MBL is parsed
          // once, on the first call only — previously every batch re-sent AND
          // re-parsed the full MBL, which is what was actually pushing
          // multi-batch jobs past the timeout (see PARSE_STATUS_ICON below for
          // the progress line shown while this runs).
          const hblFilesArray = otherFilesWithContent
          let mergedMaster: any = null
          const mergedDetails: any[] = []

          const labels = ['MBL', ...hblFilesArray.map((_, i) => `HBL ${i + 1}`)]
          const statuses: ('waiting' | 'parsing' | 'done' | 'error')[] = labels.map(() => 'waiting')
          const renderProgress = () => setParseProgress(
            labels.map((l, i) => `${l}: ${PARSE_STATUS_ICON[statuses[i]]}`).join('  |  ')
          )
          renderProgress()

          for (let i = 0; i < hblFilesArray.length; i++) {
            const hblFile = hblFilesArray[i]
            const includeMbl = i === 0 // MBL rides along with the first HBL call only
            if (includeMbl) statuses[0] = 'parsing'
            statuses[i + 1] = 'parsing'
            renderProgress()

            try {
              const payload: any = {
                mode,
                mblFile: includeMbl
                  ? { fileContent: mblFile.fileContent, mimeType: mblFile.mimeType, fileName: mblFile.fileName }
                  : null,
                hblFiles: [{ fileContent: hblFile.fileContent, mimeType: hblFile.mimeType, fileName: hblFile.fileName }]
              }

              const result = await parseDocumentAuto(payload, hblFile.mimeType, mode)

              if (result && typeof result === 'object' && 'master' in result) {
                if (includeMbl && (result as any).master) mergedMaster = (result as any).master
                mergedDetails.push(...(((result as any).details || []) as any[]))
              }
              if (includeMbl) statuses[0] = 'done'
              statuses[i + 1] = 'done'
            } catch (fileErr: any) {
              // One file's failure doesn't abort the rest — mirrors the backend's
              // own per-HBL try/catch philosophy inside a single batch.
              console.error(`[AutoJobsPage] Failed to parse ${hblFile.fileName}:`, fileErr)
              if (includeMbl) statuses[0] = 'error'
              statuses[i + 1] = 'error'
            }
            renderProgress()
          }

          setParseProgress('')

          if (mergedMaster) {
            const masterData = mergedMaster as unknown as ExtractedData
            const detailsArray = mergedDetails as unknown as ExtractedData[]
            setMasterRowData(masterData)
            setDetailRowsData(detailsArray)
            setExtractedData(masterData)
            parsed = { master: mergedMaster, details: mergedDetails }
          } else {
            setError('Failed to parse the MBL file — see console for details')
            setIsParsing(false)
            return
          }
        }
      }

      setJobRef('')  // Job ref now generated by backend
      setStep('review')
    } catch (err: any) {
      clearAutoJobsDriveToken()
      setDriveToken(null)

      if (err.message === 'UNAUTHORIZED') {
        // Token expired - trigger OAuth re-authorization flow
        sessionStorage.setItem('autojobs_oauth_state', JSON.stringify({
          mode: mode,
          dataSource: 'drive',
          driveUrl: driveFolderUrl,
          step: 'drive_preview'
        }))
        sessionStorage.setItem('autojobs_oauth_error', '401')
        initiateAutoJobsOAuth()
        return
      }

      setError(`Failed to parse files: ${err.message}`)
    } finally {
      setParseProgress('')
      setIsParsing(false)
    }
  }

  async function processDriveJobGroup(groupIndex: number) {
    if (groupIndex >= driveJobGroups.length) {
      // All groups processed
      setStep('done')
      return
    }

    setCurrentDriveGroupIndex(groupIndex)
    const group = driveJobGroups[groupIndex]
    setProgress(`Processing group ${groupIndex + 1} of ${driveJobGroups.length}...`)

    try {
      // Download and parse files based on mode
      if (mode === 'mbl_hbl') {
        // Find MBL and HBL files
        const mblFile = group.files.find(f => f.role === 'mbl')
        const hblFiles = group.files.filter(f => f.role === 'hbl')

        if (!mblFile || hblFiles.length === 0) {
          setError(`Group ${groupIndex + 1}: Missing MBL or HBL files`)
          return
        }

        const mblContent = await downloadFileAsBase64(mblFile.id, driveToken!)
        const hblContent = hblFiles.length > 0 ? await downloadFileAsBase64(hblFiles[0].id, driveToken!) : ''

        const parsed = await parseDocumentAuto(
          { mblContent, hblContent },
          '',
          'mbl_hbl'
        )

        // Handle structured response from mbl_hbl mode
        if (parsed && typeof parsed === 'object' && 'master' in parsed && parsed.master) {
          const masterData = parsed.master as unknown as ExtractedData
          const detailsArray = (parsed.details || []) as unknown as ExtractedData[]
          setMasterRowData(masterData)
          setDetailRowsData(detailsArray)
          setExtractedData(masterData)
        } else {
          // Fallback for non-structured response
          const singleData = parsed as unknown as ExtractedData
          setExtractedData(singleData)
        }
      } else if (mode === 'bundled' || mode === 'manifest' || mode === 'invoices') {
        // Multi-file processing for bundled/manifest/invoices modes
        // Separate files by role
        const mblFiles = group.files.filter(f => f.role === 'mbl')
        const hblFiles = group.files.filter(f => f.role !== 'mbl')

        if (mblFiles.length === 0) {
          setError(`Group ${groupIndex + 1}: No MBL file specified`)
          return
        }

        // For now, take first MBL (usually just 1)
        const mblFile = mblFiles[0]
        const mblContent = await downloadFileAsBase64(mblFile.id, driveToken!)

        // If there are multiple HBL/Invoice/Manifest files, process them together
        let parsed: any
        if (hblFiles.length > 0) {
          // Download all HBL files and pass them
          // For bundled/manifest/invoices, typically multiple Excel files
          // We'll download the first one and pass it as the main file
          // (API may need enhancement to handle multiple file downloads at once)
          const hblContent = await downloadFileAsBase64(hblFiles[0].id, driveToken!)
          parsed = await parseDocumentAuto(
            { mblContent, hblContent },
            hblFiles[0].mimeType,
            mode!
          )
        } else {
          // Just MBL, no HBL files
          parsed = await parseDocumentAuto(mblContent, mblFile.mimeType, mode!)
        }

        const singleData = parsed as unknown as ExtractedData
        setExtractedData(singleData)
      } else {
        // Single mode
        const file = group.files[0]
        if (!file) return

        const content = await downloadFileAsBase64(file.id, driveToken!)
        const parsed = await parseDocumentAuto(content, file.mimeType, 'single')
        const singleData = parsed as unknown as ExtractedData
        setExtractedData(singleData)
      }

      setJobRef('')  // Job ref now generated by backend
      setStep('review')
    } catch (err: any) {
      clearAutoJobsDriveToken()
      setDriveToken(null)

      if (err.message === 'UNAUTHORIZED') {
        // Token expired - trigger OAuth re-authorization flow
        sessionStorage.setItem('autojobs_oauth_state', JSON.stringify({
          mode: mode,
          dataSource: 'drive',
          driveUrl: driveFolderUrl,
          step: 'drive_preview'
        }))
        sessionStorage.setItem('autojobs_oauth_error', '401')
        initiateAutoJobsOAuth()
        return
      }

      setError(`Failed to process group ${groupIndex + 1}: ${err.message}`)
    }
  }

  // handleProcess is still used for single and mbl_hbl (same server parser as Google Drive). Its bundled / manifest / invoices branches (browser-side parsing, ending in
  // 'hbl_summary') are ENTRY REMOVED 2026-11-05, kept for reference: see the note above isHBLSheetName.
  async function handleProcess() {
    if (!mode) return

    // For mbl_hbl mode, need both files
    if (mode === 'mbl_hbl' && (!mblFile || !hblFile)) return
    // For other modes, need at least one file
    if (mode !== 'mbl_hbl' && !files.length) return

    setError('')
    setProgress('Processing files...')
    setIsParsing(true)

    try {
      // For bundled/manifest/invoices modes with multiple files, classify them
      const classifyFiles = () => {
        const pdfFiles = files.filter(f => f.type === 'application/pdf')
        const excelFiles = files.filter(f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))

        // Detect MBL file (PDF with MBL-like name, or largest PDF)
        let mblFile = pdfFiles.find(f =>
          f.name.toUpperCase().includes('MBL') ||
          f.name.toUpperCase().includes('MAWB') ||
          f.name.toUpperCase().includes('ARRIVAL') ||
          f.name.includes('B/L')
        )
        if (!mblFile && pdfFiles.length > 0) {
          mblFile = pdfFiles.reduce((a, b) => a.size > b.size ? a : b)
        }

        return { mblFile, excelFiles, otherPdfFiles: pdfFiles.filter(f => f !== mblFile) }
      }

      if ((mode === 'bundled' || mode === 'manifest' || mode === 'invoices') && files.length > 0) {
        const { excelFiles } = classifyFiles()

        // Try to process Excel files as HBL/Manifest sources
        if (excelFiles.length > 0) {
          const allHbls: HBLData[] = []
          let successCount = 0

          for (const excelFile of excelFiles) {
            try {
              setProgress(`Processing ${excelFile.name}...`)
              let hbls: HBLData[] = []

              if (mode === 'bundled') {
                hbls = await parseHBLExcel(excelFile)
              } else if (mode === 'manifest') {
                hbls = await parseAirManifestExcel(excelFile)
              } else if (mode === 'invoices') {
                // For invoices, each sheet = one invoice/HBL
                hbls = await parseHBLExcel(excelFile)
              }

              if (hbls.length > 0) {
                allHbls.push(...hbls)
                successCount++
              }
            } catch (err) {
              console.log(`[AutoJobsPage] Failed to parse ${excelFile.name}:`, err instanceof Error ? err.message : 'Unknown error')
            }
          }

          if (allHbls.length > 0) {
            // Template detection from first Excel file
            try {
              const extractedText = await extractTextFromExcelForTemplateDetection(excelFiles[0])
              const matchedTemplate = await detectTemplateByKeywords(extractedText)
              if (matchedTemplate) {
                console.log(`[AutoJobsPage] ✅ Template matched: ${matchedTemplate.carrier_agent_name}` +
                  (matchedTemplate.customer_name ? ` (customer: ${matchedTemplate.customer_name})` : '') +
                  ` [structure_type: ${matchedTemplate.structure_type || 'single_hbl'}]`)
              }
            } catch (err) {
              console.log('[AutoJobsPage] Template detection skipped')
            }

            setDetectedHBLs(allHbls)
            setSelectedHBLsForCreation(new Set(allHbls.map(h => h.sheetName)))
            setStep('hbl_summary')
            setProgress('')
            setIsParsing(false)
            return
          }
        }
      }

      // Fallback: process first file (single file or when Excel parsing fails)
      if (mode === 'bundled' && files[0] && files[0].name.endsWith('.xlsx')) {
        setProgress('Detecting HBL sheets...')
        try {
          const hbls = await parseHBLExcel(files[0])
          if (hbls.length > 0) {
            // Template detection
            try {
              const extractedText = await extractTextFromExcelForTemplateDetection(files[0])
              const matchedTemplate = await detectTemplateByKeywords(extractedText)
              if (matchedTemplate) {
                console.log(`[AutoJobsPage] ✅ Template matched: ${matchedTemplate.carrier_agent_name}` +
                  (matchedTemplate.customer_name ? ` (customer: ${matchedTemplate.customer_name})` : '') +
                  ` [structure_type: ${matchedTemplate.structure_type || 'single_hbl'}]`)
              }
            } catch (err) {
              console.log('[AutoJobsPage] Template detection skipped')
            }

            setDetectedHBLs(hbls)
            setSelectedHBLsForCreation(new Set(hbls.map(h => h.sheetName)))
            setStep('hbl_summary')
            setProgress('')
            setIsParsing(false)
            return
          }
        } catch (parseErr) {
          console.log('[AutoJobsPage] Not a multi-sheet HBL file, falling back to standard parsing')
        }
      }

      // Check if it's an air manifest Excel (manifest mode with Excel file)
      if (mode === 'manifest' && files[0] && (files[0].name.endsWith('.xlsx') || files[0].name.endsWith('.xls'))) {
        setProgress('Detecting air manifest...')
        try {
          const hawbs = await parseAirManifestExcel(files[0])
          if (hawbs.length > 0) {
            // Template detection
            try {
              const extractedText = await extractTextFromExcelForTemplateDetection(files[0])
              const matchedTemplate = await detectTemplateByKeywords(extractedText)
              if (matchedTemplate) {
                console.log(`[AutoJobsPage] ✅ Template matched: ${matchedTemplate.carrier_agent_name}` +
                  (matchedTemplate.customer_name ? ` (customer: ${matchedTemplate.customer_name})` : '') +
                  ` [structure_type: ${matchedTemplate.structure_type || 'single_hbl'}]`)
              }
            } catch (err) {
              console.log('[AutoJobsPage] Template detection skipped')
            }

            setDetectedHBLs(hawbs)
            setSelectedHBLsForCreation(new Set(hawbs.map(h => h.sheetName)))
            setStep('hbl_summary')
            setProgress('')
            setIsParsing(false)
            return
          }
        } catch (parseErr) {
          console.log('[AutoJobsPage] Not an air manifest, trying multi-HBL PDF detection:', parseErr instanceof Error ? parseErr.message : 'Unknown error')
        }
      }

      // Check if it's a multi-HBL PDF (manifest mode with PDF file)
      if (mode === 'manifest' && files[0] && files[0].type === 'application/pdf') {
        setProgress('Detecting multi-HBL PDF...')
        try {
          const hbls = await parseMultiHBLPDF(files[0])
          if (hbls.length > 0) {
            // Template detection will happen in parseMultiHBLPDF via Claude extraction
            const matchedTemplate = await detectTemplateByKeywords('BILL OF LADING NO multi-page')
            if (matchedTemplate) {
              console.log('[AutoJobsPage] Matched template:', matchedTemplate.carrier_agent_name,
                matchedTemplate.customer_name ? `(customer: ${matchedTemplate.customer_name})` : '',
                `[structure_type: ${matchedTemplate.structure_type || 'single_hbl'}]`)
            }

            setDetectedHBLs(hbls)
            setSelectedHBLsForCreation(new Set(hbls.map(h => h.sheetName)))
            setStep('hbl_summary')
            setProgress('')
            setIsParsing(false)
            return
          }
        } catch (parseErr) {
          console.log('[AutoJobsPage] Not a multi-HBL PDF, falling back to standard parsing:', parseErr instanceof Error ? parseErr.message : 'Unknown error')
        }
      }

      setStep('upload')

      if (mode === 'mbl_hbl') {
        // Handle MBL + HBL mode with two files (PDF or Excel)
        if (!mblFile || !hblFile) {
          setError('Both MBL and HBL files are required')
          setStep('upload')
          return
        }

        const mblReader = new FileReader()
        const hblReader = new FileReader()
        let mblBase64: string | null = null
        let hblBase64: string | null = null

        const processFiles = async () => {
          if (!mblBase64 || !hblBase64) return

          console.log(`[AutoJobsPage] MBL loaded: ${mblFile.name}, type: ${mblFile.type}, size: ${mblFile.size}`)
          console.log(`[AutoJobsPage] HBL loaded: ${hblFile.name}, type: ${hblFile.type}, size: ${hblFile.size}`)

          setProgress('Extracting data with AI...')

          try {
            const result = await parseDocumentAuto(
              { mblContent: mblBase64, hblContent: hblBase64, mblMimeType: mblFile.type, hblMimeType: hblFile.type },
              '', // mimeType not used for mbl_hbl
              mode
            )

            if (!result) {
              throw new Error('Failed to extract document data')
            }

            // Handle structured response from mbl_hbl mode
            if (result && typeof result === 'object' && 'master' in result && result.master) {
              const masterData = result.master as unknown as ExtractedData
              const detailsArray = (result.details || []) as unknown as ExtractedData[]
              setMasterRowData(masterData)
              setDetailRowsData(detailsArray)
              setExtractedData(masterData)
            } else {
              // Fallback for non-structured response
              const singleData = result as unknown as ExtractedData
              setExtractedData(singleData)
            }
            setEditedData(null)
            setStep('review')
            setProgress('')
          } catch (parseErr) {
            const errMsg = parseErr instanceof Error ? parseErr.message : 'Processing failed'
            setError(errMsg)
            setStep('upload')
            setProgress('')
          }
        }

        mblReader.onload = async () => {
          try {
            const dataUrl = mblReader.result as string
            const base64Parts = dataUrl.split(',')
            if (base64Parts.length !== 2) {
              throw new Error('Invalid MBL file encoding')
            }
            mblBase64 = base64Parts[1]
            if (mblBase64 && hblBase64) await processFiles()
          } catch (err) {
            const errMsg = err instanceof Error ? err.message : 'Failed to read MBL file'
            setError(errMsg)
            setStep('upload')
            setProgress('')
          }
        }

        hblReader.onload = async () => {
          try {
            const dataUrl = hblReader.result as string
            const base64Parts = dataUrl.split(',')
            if (base64Parts.length !== 2) {
              throw new Error('Invalid HBL file encoding')
            }
            hblBase64 = base64Parts[1]
            if (mblBase64 && hblBase64) await processFiles()
          } catch (err) {
            const errMsg = err instanceof Error ? err.message : 'Failed to read HBL file'
            setError(errMsg)
            setStep('upload')
            setProgress('')
          }
        }

        mblReader.readAsDataURL(mblFile)
        hblReader.readAsDataURL(hblFile)
      } else if (mode === 'invoices') {
        // Handle commercial invoice mode
        const file = files[0]
        try {
          // Check if it's a Chinese customs invoice Excel format
          if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel') {
            setProgress('Detecting Chinese customs invoice format...')

            // First try template detection for 发票箱单 format
            try {
              const extractedText = await extractTextFromExcelForTemplateDetection(file)
              const matchedTemplate = await detectTemplateByKeywords(extractedText)
              if (matchedTemplate && matchedTemplate.carrier_agent_name === '发票箱单') {
                console.log('[AutoJobsPage] ✅ Template matched: 发票箱单')
                const invoiceBoxData = await parseInvoiceBoxTemplate(file)
                setExtractedData(invoiceBoxData)
                setEditedData(null)
                setStep('review')
                setProgress('')
                setIsParsing(false)
                return
              }
            } catch (templateErr) {
              console.log('[AutoJobsPage] Template detection for 发票箱单 skipped or not matched')
            }

            // Fall back to standard Chinese customs invoice format
            try {
              const chineseInvoiceData = await parseChineseInvoiceExcel(file)
              setExtractedData(chineseInvoiceData)
              setEditedData(null)
              setStep('review')
              setProgress('')
              setIsParsing(false)
              return
            } catch (parseErr) {
              console.log('[AutoJobsPage] Not a Chinese customs invoice, using standard invoice parser')
            }
          }

          // Fall back to standard invoice parsing with Claude AI
          setProgress('Extracting invoice text...')
          let extractedText = ''

          if (file.type === 'application/pdf') {
            extractedText = await extractTextFromPDF(file)
          } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel') {
            extractedText = await extractTextFromExcel(file)
          } else {
            throw new Error('Unsupported file type for invoice parsing')
          }

          if (!extractedText.trim()) {
            throw new Error('Could not extract text from invoice file')
          }

          setProgress('Parsing invoice with AI...')
          const invoiceData = await parseInvoiceWithClaude(extractedText)

          // Map invoice data to ExtractedData format
          const mapped: ExtractedData = {
            transport_mode: 'SEA', // Default, user can override
            direction: 'IMP', // Default import, user can override
            doc_type: 'INVOICE',
            mbl_number: invoiceData.header.bill_no || undefined,
            shipper: { name: invoiceData.header.shipper || undefined },
            consignee: {
              name: invoiceData.header.consignee || undefined,
              address: invoiceData.header.consignee_address || undefined
            },
            ports: {
              loading: invoiceData.header.port_of_loading || undefined,
              discharge: invoiceData.header.port_of_discharge || undefined
            },
            container: {
              packages: invoiceData.header.packages?.toString() || undefined
            },
            cargo: {
              description: invoiceData.pivot.map(p => p.goods_description).join(', ') || undefined,
              pieces: invoiceData.header.packages || undefined,
              gross_weight_kgs: invoiceData.header.gross_weight_kg || undefined,
              net_weight_kgs: invoiceData.header.net_weight_kg || undefined
            },
            summary: `Invoice #${invoiceData.header.invoice_no || 'N/A'} - ${invoiceData.pivot.length} line items`
          }

          setExtractedData(mapped)
          setEditedData(null)
          setStep('review')
          setProgress('')
          setIsParsing(false)
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : 'Failed to parse invoice'
          setError(errMsg)
          setStep('upload')
          setProgress('')
          setIsParsing(false)
        }
      } else {
        // Handle single file modes (single document, manifest, etc.)
        const file = files[0]
        const reader = new FileReader()

        reader.onload = async () => {
          try {
            const dataUrl = reader.result as string
            const base64Parts = dataUrl.split(',')

            if (base64Parts.length !== 2) {
              throw new Error('Invalid file encoding - could not extract base64 data')
            }

            const base64 = base64Parts[1]
            console.log(`[AutoJobsPage] File loaded: ${file.name}, size: ${file.size}, base64 length: ${base64.length}`)

            setProgress('Extracting data with AI...')

            try {
              const result = await parseDocumentAuto(
                base64,
                file.type,
                mode
              )

              if (!result) {
                throw new Error('Failed to extract document data')
              }

              // Handle structured response from mbl_hbl mode
              if (result && typeof result === 'object' && 'master' in result && result.master) {
                const masterData = result.master as unknown as ExtractedData
                const detailsArray = (result.details || []) as unknown as ExtractedData[]
                setMasterRowData(masterData)
                setDetailRowsData(detailsArray)
                setExtractedData(masterData)
              } else {
                // Fallback for non-structured response
                const singleData = result as unknown as ExtractedData
                setExtractedData(singleData)
              }
              setEditedData(null)
              setStep('review')
              setProgress('')
            } catch (parseErr) {
              const errMsg = parseErr instanceof Error ? parseErr.message : 'Processing failed'
              setError(errMsg)
              setStep('upload')
              setProgress('')
              setIsParsing(false)
            }
          } catch (err) {
            const errMsg = err instanceof Error ? err.message : 'Processing failed'
            setError(errMsg)
            setStep('upload')
            setProgress('')
            setIsParsing(false)
          }
        }

        reader.readAsDataURL(file)
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Error reading file'
      setError(errMsg)
      setStep('upload')
      setProgress('')
      setIsParsing(false)
    }
  }

  async function checkAndPromptForCustomers(): Promise<{ customers: Record<string, string>; needsModal: boolean }> {
    if (!editData) return { customers: {}, needsModal: false }

    const pending: Array<{ type: 'shipper' | 'consignee'; name: string; address?: string; matchedCustomers: any[] }> = []
    const foundCustomers: Record<string, string> = {}

    // Check shipper
    const shipperName = typeof editData.shipper === 'string' ? editData.shipper : editData.shipper?.name
    if (shipperName) {
      const { data: matches } = await supabase
        .from('customers')
        .select('id, code, name, address, country')
        .ilike('name', `%${shipperName}%`)
        .eq('is_active', true)

      if (!matches || matches.length === 0) {
        pending.push({
          type: 'shipper',
          name: shipperName,
          address: typeof editData.shipper === 'string' ? undefined : editData.shipper?.address,
          matchedCustomers: []
        })
      } else if (matches.length === 1) {
        foundCustomers.shipper = matches[0].id
      } else {
        pending.push({
          type: 'shipper',
          name: shipperName,
          address: typeof editData.shipper === 'string' ? undefined : editData.shipper?.address,
          matchedCustomers: matches
        })
      }
    }

    // Check consignee
    const consigneeName = typeof editData.consignee === 'string' ? editData.consignee : editData.consignee?.name
    if (consigneeName) {
      const { data: matches } = await supabase
        .from('customers')
        .select('id, code, name, address, country')
        .ilike('name', `%${consigneeName}%`)
        .eq('is_active', true)

      if (!matches || matches.length === 0) {
        pending.push({
          type: 'consignee',
          name: consigneeName,
          address: typeof editData.consignee === 'string' ? undefined : editData.consignee?.address,
          matchedCustomers: []
        })
      } else if (matches.length === 1) {
        foundCustomers.consignee = matches[0].id
      } else {
        pending.push({
          type: 'consignee',
          name: consigneeName,
          address: typeof editData.consignee === 'string' ? undefined : editData.consignee?.address,
          matchedCustomers: matches
        })
      }
    }

    if (pending.length > 0) {
      setPendingCustomers(pending)
      setCustomerAction({})
      setSelectedCustomers(foundCustomers)
      setAddressCheckModal(true)
      return { customers: foundCustomers, needsModal: true }
    }

    return { customers: foundCustomers, needsModal: false }
  }

  async function handleCreateJob() {
    if (!editData || !jobRef.trim()) {
      setError('Job reference is required')
      return
    }

    // Check for customers first
    const { customers: foundCustomers, needsModal } = await checkAndPromptForCustomers()
    if (needsModal) {
      // Modal will be shown, and handleAddressModalSubmit will continue
      return
    }

    // No modal needed, proceed with job creation
    proceedWithJobCreation(foundCustomers)
  }

  async function syncJobToBoxtrax(jobId: string, invoiceToConfirmed = false) {
    if (!BOXTOP_SYNC_ENABLED) return   // BoxTop sync is off (lib/boxtopSync.ts)
    try {
      setBoxtraxError('')
      console.log('[AutoJobsPage] Syncing job to Boxtrax:', jobId, 'invoiceToConfirmed:', invoiceToConfirmed)

      // Prepare sync data
      const syncData = {
        job_ref: jobRef,
        invoiceToConfirmed,
        waybill_number: editData?.waybill_number,
        mbl_number: editData?.mbl_number,
        transport_mode: editData?.transport_mode,
        direction: editData?.direction,
        shipper: editData?.shipper,
        consignee: editData?.consignee,
        ports: editData?.ports,
        cargo: editData?.cargo
      }

      console.log('[AutoJobsPage] Sending to Boxtrax:', syncData)

      // Call Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-to-boxtrax`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`
          },
          body: JSON.stringify(syncData)
        }
      )

      const result = await response.json()
      console.log('[AutoJobsPage] Boxtrax sync response:', result)

      if (result.result === 'invoice_to_not_bound') {
        setBoxtraxSyncStatus('error')
        setBoxtraxError(result.error || 'Customer has no BoxTop account bound')
        return
      }
      if (result.result === 'address_not_linked') {
        setBoxtraxSyncStatus('error')
        setBoxtraxError(result.error || 'Link the Shipper and Consignee addresses in the Sync Preflight before syncing')
        return
      }
      if (result.result === 'address_not_chosen') {
        setBoxtraxSyncStatus('error')
        setBoxtraxError(result.error || 'Choose the address for each party on the job before syncing')
        return
      }
      if (result.result === 'port_code_missing') {
        // Nothing was sent to BoxTop -- show why and do NOT stamp a fake reference on the job.
        setBoxtraxSyncStatus('error')
        setBoxtraxError(result.error || 'A port has no usable code -- not synced')
        return
      }
      if (result.result === 'invoice_to_address_mismatch' || result.result === 'ambiguous_contact_match') {
        // Rare race: BoxTop's data changed between the preflight check and this call (this
        // function is only ever called with invoiceToConfirmed:true, after the Sync Preflight
        // modal was already approved).
        setBoxtraxSyncStatus('error')
        setBoxtraxError('BoxTop data changed since preflight was approved — please retry sync from the job detail page')
        return
      }

      const isSuccess = result.success && !result.error
      const boxtraxRef = result.boxtraxRef || `SYNC-${Date.now()}`
      const syncError = result.error || (result.success ? null : 'Failed to sync with Boxtrax')

      // Update job record with sync status
      const { error: updateError } = await supabase
        .from('jobs')
        .update({
          boxtrax_synced: isSuccess,
          boxtrax_ref: boxtraxRef,
          boxtrax_synced_at: new Date().toISOString(),
          boxtrax_error: syncError
        })
        .eq('id', jobId)

      if (updateError) {
        console.error('[AutoJobsPage] Error updating job with Boxtrax status:', updateError)
      }

      // Update UI state
      setBoxtraxRef(boxtraxRef)
      setBoxtraxSyncStatus(isSuccess ? 'success' : result.error ? 'warning' : 'error')
      if (syncError) {
        setBoxtraxError(syncError)
      }
    } catch (err) {
      console.error('[AutoJobsPage] Boxtrax sync error:', err)
      const errorMsg = err instanceof Error ? err.message : 'Boxtrax sync failed'
      setBoxtraxSyncStatus('error')
      setBoxtraxError(errorMsg)

      // Update job record with error
      if (createdJobId) {
        await supabase
          .from('jobs')
          .update({
            boxtrax_synced: false,
            boxtrax_synced_at: new Date().toISOString(),
            boxtrax_error: errorMsg
          })
          .eq('id', createdJobId)
      }
    }
  }


  const getSelectedModeColor = () => {
    const modeObj = PROCESSING_MODES.find(m => m.id === mode)
    return modeObj?.color || ''
  }

  // Auto-skip format selection for multi-file modes
  // All multi-file modes (bundled, manifest, invoices, mbl_hbl) skip format and go to upload
  // Users specify document types via Role dropdown after scanning files
  useEffect(() => {
    if (step === 'format' && mode && mode !== 'single') {
      setStep('upload')
      setJobRef('')
      resetBoxtraxState()
      setDetectedHBLs([])
      setSelectedHBLsForCreation(new Set())
    }
  }, [step, mode])

  // Auto-enter drive_input when uploading with drive data source. A mode without local upload (see lib/autoJobsModes.ts) ALWAYS goes to Drive, whatever the
  // data source was left at by an earlier mode (e.g. 'local' set by Back while in single mode).
  useEffect(() => {
    if (step === 'upload' && (dataSource === 'drive' || (mode && !supportsLocalUpload(mode)))) {
      if (dataSource !== 'drive') setDataSource('drive')
      setStep('drive_input')
      setShowDataSourcePicker(false)
    }
  }, [step, dataSource, mode])

  // Initialize job ref when entering jobref step
  useEffect(() => {
    if (step === 'jobref' && !jobRef) {
      setJobRef('')  // Job ref now generated by backend
    }
  }, [step])

  function resetBoxtraxState() {
    setBoxtraxRef(null)
    setBoxtraxSyncStatus('pending')
    setBoxtraxError('')
  }

  // Phase 2: Address validation & Job creation handlers
  function handleAddressDecisions(decisions: any[], customerMappings?: any[]) {
    setAddressDecisions(decisions)
    if (customerMappings) {
      setCustomerMappings(customerMappings)
    }
    // Parent ref is generated by Edge Function, not frontend
    // Show placeholder in dialog with message "will be assigned upon creation"
    setPlannedParentJobRef('')
    setShowJobConfirmDialog(true)
  }

  async function handleJobConfirm(prefill?: JobCreationPrefill) {
    // Guard against multiple calls
    if (jobCreationLoading) {
      console.log('[handleJobConfirm] Already in progress, ignoring duplicate call')
      return
    }

    console.log('[handleJobConfirm] Start', {
      mode,
      masterExists: !!masterRowData || !!extractedData,
      detailsCount: detailRowsData.length,
      decisionsCount: addressDecisions.length,
    })

    if (!masterRowData && !extractedData) {
      console.error('[handleJobConfirm] No master data found')
      setError('No master data found')
      return
    }

    setJobCreationLoading(true)
    setError('')

    try {
      // Z48/Z43: mode 'single' never calls setMasterRowData -- MBLHBLTable's onDataChange
      // (AutoJobsPage.tsx ~3641) only writes edits there `if (masterRowData)` is already
      // truthy, else it falls through to setEditedData. So for 'single' mode, every wizard
      // edit (Freight Term/Type, Container Type, Packages, ...) landed in `editedData` and
      // was silently discarded here, since this used to read straight from `extractedData`
      // (the pre-edit AI parse) -- editedData must come before that raw fallback.
      const baseMaster = masterRowData || editedData || extractedData
      // Task J: pre-fill fields are merged in here (not written back via setState) so
      // create-autojobs always sees them on this same request, without a stale-closure
      // race against React's async state updates.
      const master = prefill
        ? { ...baseMaster, ...prefill, ports: { ...baseMaster?.ports, ...prefill.ports } }
        : baseMaster
      const details = detailRowsData.length > 0 ? detailRowsData : []

      // Y-followup (task 22, bug F): dialog-selected Invoice To / Overseas Agent / Port Agent
      // ride along as customerMappings entries (same channel create-autojobs already reads
      // master_invoice_to/master_overseas_agent from) rather than a new request field. A
      // dialog selection overrides whatever address-validation step may have resolved for
      // the same role, since it's the more recent, explicit staff choice.
      const prefillMappings: { key: string; customerId: string }[] = []
      if (prefill?.invoiceToId) prefillMappings.push({ key: 'master_invoice_to', customerId: prefill.invoiceToId })
      if (prefill?.overseasAgentId) prefillMappings.push({ key: 'master_overseas_agent', customerId: prefill.overseasAgentId })
      if (prefill?.portAgentId) prefillMappings.push({ key: 'master_port_agent', customerId: prefill.portAgentId })
      const mergedCustomerMappings = prefillMappings.length > 0
        ? [...customerMappings.filter(m => !prefillMappings.some(p => p.key === m.key)), ...prefillMappings]
        : customerMappings

      console.log('[handleJobConfirm] Calling create-autojobs API', {
        mode,
        masterDoc: master?.doc_type,
        detailCount: details.length,
        addressDecisionsCount: addressDecisions.length,
        apiUrl: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-autojobs`,
      })

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-autojobs`,
        {
          method: 'POST',
          headers: {
            // create-autojobs has verify_jwt on, so this must be the signed-in user's JWT --
            // a publishable/anon key is not a JWT and the gateway would reject it.
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token ?? ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mode,
            master,
            details,
            addressDecisions,
            customerMappings: mergedCustomerMappings,
            // The ref locked in the job-ref step (incl. "Use this" from a pre-reservation) --
            // create-autojobs must use exactly this, not generate a new one.
            job_ref: userJobRef || undefined,
            // #290: lets create-autojobs stamp jobs.created_by with the real acting user
            // (was always the literal 'system') so it can resolve to a BoxTop Owner later.
            // Y2: staff can override this via the pre-fill dialog's Created By dropdown.
            created_by: prefill?.created_by || userEmail || 'system',
          }),
        }
      )

      console.log('[handleJobConfirm] API Response status:', response.status, response.statusText)

      const result = await response.json()
      console.log('[handleJobConfirm] API Response body:', result)

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create jobs')
      }

      console.log('[handleJobConfirm] Jobs created successfully', {
        parentRef: result.parent_job_ref,
        childCount: result.child_job_refs?.length || 0,
      })

      // Save result for Step 7
      setCreatedParentJobRef(result.parent_job_ref)
      setCreatedParentJobId(result.parent_job_id)
      setCreatedChildJobIds(result.child_job_ids || [])
      setCreatedChildJobRefs(result.child_job_refs || [])
      setCreatedCustomers(result.customers_created || [])

      // Z36 redo: the reservation (if any) was retired by the database in the same transaction as the job insert.

      // #16: no auto-sync on create -- Gary confirmed a job auto-synced to BoxTop (KI26032)
      // even though he never pressed Sync. Staff must always trigger BoxTop sync explicitly
      // via the Job Detail Sync button; new jobs land with sync_status defaulting to
      // 'pending' (see create-autojobs' jobs insert / the jobs table default).

      // Move to result step
      setShowJobConfirmDialog(false)
      setStep('job_result')
    } catch (err) {
      console.error('[handleJobConfirm] Error:', err)
      setError(err instanceof Error ? err.message : 'Failed to create jobs')
    } finally {
      setJobCreationLoading(false)
    }
  }

  function handleStartOver() {
    // Reset everything
    setStep('mode')
    setMode(null)
    setFiles([])
    setMasterRowData(null)
    setDetailRowsData([])
    setExtractedData(null)
    setEditedData(null)
    setError('')
    setProgress('')
    setAddressDecisions([])
    setPlannedParentJobRef(null)
    setCreatedParentJobRef(null)
    setCreatedParentJobId(null)
    setCreatedChildJobIds([])
    setCreatedChildJobRefs([])
    setCreatedCustomers([])
  }

  async function handleAddressModalSubmit() {
    try {
      setProgress('Processing customer information...')
      // Start with already-selected customers (single matches found earlier)
      const finalSelectedCustomers: Record<string, string> = { ...selectedCustomers }

      // Create new customers if needed or handle selections
      for (const pending of pendingCustomers) {
        const action = customerAction[pending.type]

        if (action === 'create') {
          // Create new customer
          const address = pending.type === 'shipper'
            ? (editData?.ports?.loading || pending.address || '')
            : (editData?.ports?.discharge || pending.address || '')

          const { data: newCustomer, error: createError } = await supabase
            .from('customers')
            .insert({
              code: generateFallbackCustomerCode(pending.name, pending.type === 'shipper' ? 'SHIP' : 'CONS'),
              name: pending.name,
              address: address || '',
              country: pending.type === 'shipper' ? editData?.origin_country : editData?.dest_country,
              currency: 'GBP',
              is_active: true
            })
            .select()
            .single()

          if (createError) throw createError
          finalSelectedCustomers[pending.type] = newCustomer.id
        } else if (action === 'select') {
          // User selected a customer from the dropdown
          if (selectedCustomers[pending.type]) {
            finalSelectedCustomers[pending.type] = selectedCustomers[pending.type]
          }
        } else if (action === 'skip') {
          // Skip - don't set any customer
          delete finalSelectedCustomers[pending.type]
        }
      }

      setAddressCheckModal(false)
      setProgress('')
      // Now proceed with job creation, passing the selected customers
      proceedWithJobCreation(finalSelectedCustomers)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process customer information')
      setProgress('')
    }
  }

  async function proceedWithJobCreation(selectedCustomersParam?: Record<string, string>) {
    if (!editData || !jobRef.trim()) {
      setError('Job reference is required')
      return
    }

    setStep('creating')
    setProgress('Creating job from extracted data...')
    setError('')

    try {
      await performJobCreation(selectedCustomersParam || selectedCustomers)
    } catch (err) {
      console.error('[AutoJobsPage] Error creating job:', err)
      const errMsg = err instanceof Error ? err.message : 'Failed to create job'
      setError(errMsg)
      setStep('review')
      setProgress('')
    }
  }

  async function performJobCreation(customersToUse: Record<string, string> = {}) {
    if (!editData) throw new Error('No data to create job')

    // Use extracted transport_mode and direction from AI, with fallbacks
    let transportMode = (editData.transport_mode as string) || 'SEA'
    let direction = (editData.direction as string) || 'IMP'

    // Fallback: determine from document type if not extracted
    if (!editData.transport_mode) {
      const isAir = editData.waybill_number && !editData.mbl_number
      transportMode = isAir ? 'AIR' : 'SEA'
    }

    // Determine job_type based on document type
    const docType = (editData.doc_type as string) || ''
    let jobType = 'SINGLE'

    if (docType === 'MBL' || docType === 'MAWB') {
      jobType = 'CONSOL'
    } else if (docType === 'HBL' || docType === 'HAWB') {
      jobType = 'SINGLE'
    } else if (!docType) {
      if (editData.mbl_number && !editData.waybill_number) {
        jobType = 'CONSOL'
      } else {
        jobType = 'SINGLE'
      }
    }

    const today = new Date().toISOString().split('T')[0]
    const jobData = {
      job_ref: jobRef.trim(),
      direction: direction.toUpperCase(),
      transport_mode: transportMode.toUpperCase(),
      job_type: jobType,
      // K4: AutoJob-created jobs start with Customs Team, not Pre-Alert -- matches the
      // create-autojobs edge function default fixed in #10/#299.
      status: 'CUS' as const,
      booked_date: today,
      created_by: userEmail || 'AUTOJOBS',
      importer_id: customersToUse.shipper || null,
      consignor_id: customersToUse.consignee || null
    }

    // Validate required fields
    if (!jobData.job_ref?.trim()) {
      throw new Error('job_ref is required and cannot be empty')
    }
    if (!jobData.direction || !['IMP', 'EXP'].includes(jobData.direction)) {
      throw new Error(`direction must be IMP or EXP, got: ${jobData.direction}`)
    }
    if (!jobData.transport_mode || !['AIR', 'SEA', 'ROAD', 'COURIER', 'DOMESTIC'].includes(jobData.transport_mode)) {
      throw new Error(`transport_mode must be AIR/SEA/ROAD/COURIER/DOMESTIC, got: ${jobData.transport_mode}`)
    }
    if (!jobData.job_type || !['SINGLE', 'CONSOL'].includes(jobData.job_type)) {
      throw new Error(`job_type must be SINGLE or CONSOL, got: ${jobData.job_type}`)
    }

    const { data: job, error: jobError } = await supabase
      .from('jobs')
      .insert(jobData)
      .select()
      .single()

    if (jobError) {
      throw new Error(`Failed to create job: ${jobError.message}${jobError.details ? ` (${jobError.details})` : ''}${jobError.hint ? ` [${jobError.hint}]` : ''}`)
    }

    if (!job) {
      throw new Error('Job created but no data returned')
    }

    setCreatedJobId(job.id)

    // Insert shipping details
    if (transportMode.toUpperCase() === 'SEA') {
      const seaDetailsData: any = {
        job_id: job.id,
        vessel_name: editData.vessel_name || null,
        voyage_no: editData.voyage_number || null,
        house_bl_no: editData.waybill_number || null,
        master_bl_no: editData.mbl_number || null,
        place_of_loading: editData.ports?.loading || null,
        final_destination: editData.ports?.discharge || null,
        shipping_terms_id: null,
        ai_parsed_data: {
          shipper: editData.shipper,
          consignee: editData.consignee,
          carrier: editData.carrier,
          origin_country: editData.origin_country,
          dest_country: editData.dest_country,
          eta: editData.eta,
          etd: editData.etd,
          cargo: editData.cargo,
          container: editData.container,
          ports: editData.ports,
          import_source: 'AUTOJOBS'
        }
      }

      if (editData.eta) seaDetailsData.arrival_date = editData.eta
      if (editData.etd) seaDetailsData.departure_date = editData.etd

      await supabase.from('job_sea_details').insert(seaDetailsData)

      // Insert container row (#291) if a container number was extracted
      if (editData.container?.number) {
        const piecesStr = editData.container?.packages
        const noOfPcs = piecesStr ? parseInt(piecesStr, 10) : editData.cargo?.pieces
        const containerData: any = {
          job_id: job.id,
          container_type_id: null,
          container_no: editData.container?.number || null,
          seal_no: editData.container?.seal || null,
          no_of_pcs: Number.isFinite(noOfPcs) ? noOfPcs : null,
          gross_weight: editData.cargo?.gross_weight_kgs || null
        }
        await supabase.from('job_containers').insert(containerData)
      }
    } else if (transportMode.toUpperCase() === 'AIR') {
      const airDetailsData: any = {
        job_id: job.id,
        flight_no: editData.flight_vessel || null,
        mawb_no: editData.mbl_number || null,
        hawb_no: editData.waybill_number || null,
        ai_parsed_data: {
          shipper: editData.shipper,
          consignee: editData.consignee,
          carrier: editData.carrier,
          origin_country: editData.origin_country,
          dest_country: editData.dest_country,
          eta: editData.eta,
          etd: editData.etd,
          cargo: editData.cargo,
          import_source: 'AUTOJOBS'
        }
      }

      if (editData.eta) airDetailsData.arrival_date = editData.eta
      if (editData.etd) airDetailsData.departure_date = editData.etd

      await supabase.from('job_air_details').insert(airDetailsData)
    }

    // Insert goods/cargo information if available
    if (editData.cargo) {
      const goodsData: any = {
        job_id: job.id,
        item_no: 1,
        description: editData.cargo.description || '',
        pieces: editData.cargo.pieces || null,
        gross_weight_kgs: editData.cargo.gross_weight_kgs || null,
        net_weight_kgs: editData.cargo.net_weight_kgs || null,
        volume_cbm: editData.cargo.volume_cbm || null,
        chargeable_weight: editData.cargo.chargeable_weight || null
      }

      // Only insert if we have meaningful data
      if (goodsData.description || goodsData.pieces || goodsData.gross_weight_kgs) {
        await supabase.from('job_goods').insert(goodsData)
      }
    }

    // Check if there are more Drive Job Groups to process
    if (driveJobGroups.length > 0 && currentDriveGroupIndex + 1 < driveJobGroups.length) {
      processDriveJobGroup(currentDriveGroupIndex + 1)
    } else {
      setStep('done')
    }

    setProgress('')
    if (BOXTOP_SYNC_ENABLED) setPendingPreflight({ jobId: job.id })   // used to open the BoxTop Sync Preflight after every create
  }

  function resetFormForNewJob() {
    setStep('mode')
    setMode(null)
    setFileFormat(null)
    setFiles([])
    setMblFile(null)
    setHblFile(null)
    setError('')
    setProgress('')
    setExtractedData(null)
    setEditedData(null)
    setMasterRowData(null)
    setDetailRowsData([])
    setJobRef('')
    setCreatedJobId(null)
    resetBoxtraxState()
    setDetectedHBLs([])
    setSelectedHBLsForCreation(new Set())
    setIsParsing(false)
    setAddressCheckModal(false)
    setPendingCustomers([])
    setCustomerAction({})
    setSelectedCustomers({})
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/jobs')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Jobs
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AutoJobs</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Batch import jobs with AI extraction</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8">
          {(['mode', 'format', 'upload', 'review', 'hbl_summary', 'jobref', 'creating', 'done'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-blue-600 text-white'
                    : ['format', 'upload', 'review', 'hbl_summary', 'jobref', 'creating', 'done'].indexOf(step) > i
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                }`}
              >
                {i + 1}
              </div>
              {i < 7 && <div className="w-8 h-0.5 bg-gray-200 dark:bg-gray-700" />}
            </div>
          ))}
        </div>

        {/* Summary Bar - Show on all steps after mode selection */}
        {step !== 'mode' && step !== 'done' && (
          <div className="sticky top-6 z-10 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              {mode && (
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${getSelectedModeColor()}`} />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-semibold text-gray-900 dark:text-white">Mode:</span> {PROCESSING_MODES.find(m => m.id === mode)?.label}
                  </span>
                </div>
              )}
              {fileFormat && (
                <div className="flex items-center gap-2 pl-4 border-l border-gray-300 dark:border-gray-600">
                  <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                  </svg>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-semibold text-gray-900 dark:text-white">Format:</span> {fileFormat.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Step 1: Mode Selection */}
        {/* Job Reference Preview Step */}
        {step === 'job_ref_preview' && mode && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-2xl mx-auto">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              📋 Next Job Reference
            </h2>
            {lockedReservation && (
              <div className="mb-4 rounded-lg border border-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-700 px-4 py-3 text-sm text-teal-900 dark:text-teal-200">
                Using reserved number <span className="font-mono font-bold">{lockedReservation.job_ref}</span>. It cannot be changed in this flow.
              </div>
            )}

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900 rounded-lg p-6 mb-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Reference
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={userJobRef || ''}
                      onChange={(e) => handleJobRefChange(e.target.value)}
                      readOnly={!!lockedReservation}
                      disabled={loadingJobRef}
                      placeholder="e.g., KI26001"
                      className={`flex-1 px-4 py-2 rounded-lg border-2 font-mono text-lg font-bold transition-colors ${
                        jobRefError
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
                      }`}
                    />
                    {!lockedReservation && <button
                      onClick={() => {
                        const direction = 'IMP'  // All current modes are import
                        loadJobRefPreview(direction)
                      }}
                      disabled={loadingJobRef}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 rounded-lg transition-colors"
                    >
                      🔄
                    </button>}
                  </div>
                  {jobRefError && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2">❌ {jobRefError}</p>
                  )}
                  {!jobRefError && jobRefWarning && jobRefWarning.ref !== dismissedWarningRef && (
                    <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg text-sm text-amber-800 dark:text-amber-300">
                      <p>
                        <span className="font-mono font-bold">{jobRefWarning.ref}</span> is pre-reserved by{' '}
                        <span className="font-medium">{jobRefWarning.reservedBy}</span> since {formatRelativeTime(jobRefWarning.reservedAt)}.
                        {jobRefWarning.note && <> Note: "{jobRefWarning.note}".</>}
                      </p>
                      <div className="mt-2 flex gap-4">
                        <button type="button" onClick={handleSkipToAvailable} className="text-xs font-semibold underline hover:no-underline">
                          Skip to next available
                        </button>
                        <button type="button" onClick={handleUseAnyway} className="text-xs font-semibold underline hover:no-underline">
                          Use anyway
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {loadingJobRef ? (
                      '⏳ Loading next reference...'
                    ) : previewJobRef ? (
                      <>
                        <span className="font-medium">System suggests:</span>{' '}
                        <span className="font-mono font-bold text-gray-900 dark:text-white">{previewJobRef}</span>
                        {userJobRef && userJobRef !== previewJobRef && (
                          <span className="ml-2 text-blue-600 dark:text-blue-400">
                            (modified by user)
                          </span>
                        )}
                      </>
                    ) : (
                      'Failed to load preview'
                    )}
                  </p>
                </div>

                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Format: KI/KE + 5 digits. To use a custom number, edit the field above. Otherwise, click Continue to proceed with the suggested number.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-between">
              <button
                onClick={() => {
                  setStep('mode')
                  setUserJobRef(null)
                  setPreviewJobRef(null)
                  setJobRefError(null)
                  setJobRefWarning(null)
                  setDismissedWarningRef(null)
                }}
                className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={async () => {
                  const success = await applyUserJobRef()
                  if (success) {
                    // Proceed based on format selection needed
                    if (mode === 'single') {
                      setStep('format')
                    } else {
                      setStep('upload')
                    }
                  }
                }}
                disabled={!validateJobRef(userJobRef || '') || loadingJobRef || !!jobRefError}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {step === 'mode' && (
          <div className="space-y-4">
            {lockedReservation && (
              <div className="rounded-lg border border-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-700 px-4 py-3 text-sm text-teal-900 dark:text-teal-200">
                Using reserved number <span className="font-mono font-bold">{lockedReservation.job_ref}</span>. It cannot be changed in this flow.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PROCESSING_MODES.map(m => (
                <button
                  key={m.id}
                  onClick={async () => {
                    setMode(m.id as ProcessingMode)
                    setStep('job_ref_preview')
                    // Load job reference preview (all current modes are IMP - import)
                    const direction = 'IMP'
                    setTimeout(() => loadJobRefPreview(direction), 100)
                  }}
                  className={`p-6 rounded-xl border-2 transition-all text-left hover:shadow-lg relative ${
                    mode === m.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  }`}
                >
                  {mode === m.id && (
                    <div className="absolute top-3 right-3 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${m.color} mb-3`} />
                  <h3 className="font-semibold text-gray-900 dark:text-white">{m.label}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{m.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Format Selection (only for single file modes) */}
        {step === 'format' && mode === 'single' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Select File Format
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => {
                  setFileFormat('pdf')
                  setStep('upload')
                }}
                className={`p-6 rounded-lg border-2 transition-all ${
                  fileFormat === 'pdf'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <svg className="w-8 h-8 text-red-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.5 13.5L11 16l5-5M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                </svg>
                <p className="font-medium text-gray-900 dark:text-white">PDF</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">(PDF)</p>
              </button>
              <button
                onClick={() => {
                  setFileFormat('xlsx')
                  setStep('upload')
                }}
                className={`p-6 rounded-lg border-2 transition-all ${
                  fileFormat === 'xlsx'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <svg className="w-8 h-8 text-green-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V9h2v8zm4 0h-2v-4h2v4z" />
                </svg>
                <p className="font-medium text-gray-900 dark:text-white">Excel</p>
              </button>
            </div>
            <button
              onClick={() => setStep('mode')}
              className="mt-6 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              ← Back
            </button>
          </div>
        )}

        {/* Data Source Toggle (after format step, before upload) */}
        {/* Only the modes that still have a local option (single, mbl_hbl) get this choice. For the others Step 3 goes straight to the Drive folder step: no one-option picker. */}
        {step === 'upload' && mode && supportsLocalUpload(mode) && (mode !== 'single' || fileFormat) && showDataSourcePicker && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Choose Data Source</h2>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setDataSource('drive')
                  setStep('drive_input')
                }}
                className="flex-1 p-4 rounded-lg border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20 transition-colors relative"
              >
                <div className="absolute top-2 right-3 px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded">
                  Recommended
                </div>
                <div className="font-medium text-gray-900 dark:text-white">🔗 Google Drive Folder</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Scan files from Google Drive</div>
              </button>
              <button
                onClick={() => setShowDataSourcePicker(false)}
                className="flex-1 p-4 rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 transition-colors"
              >
                <div className="font-medium text-gray-900 dark:text-white">📤 Upload Local Files</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Upload files from your computer</div>
              </button>
            </div>
          </div>
        )}

        {/* Step 3a: Drive Input */}
        {step === 'drive_input' && mode && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Google Drive Folder</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Folder Link
                </label>
                <input
                  type="text"
                  value={driveFolderUrl}
                  onChange={e => setDriveFolderUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/YOUR_FOLDER_ID"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Paste the Google Drive folder link that you want to scan
                </p>
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setStep('mode')
                  // Back used to switch the data source to 'local' for EVERY mode: the hidden way into the local upload. Now only for the modes that still have it.
                  if (supportsLocalUpload(mode)) setDataSource('local')
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={handleScanDriveFolder}
                disabled={!driveFolderUrl || driveScanLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {driveScanLoading ? 'Scanning...' : 'Scan Folder'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3b: Drive Preview & Grouping */}
        {step === 'drive_preview' && mode && driveToken && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <DriveGroupingPanel
              files={driveFolderFiles}
              mode={mode}
              showAllTypes={showAllFileTypes}
              folderName={driveFolderUrl.split('folders/')[1]?.substring(0, 20)}
              isParsing={isParsing}
              parseProgress={parseProgress}
              onToggleShowAll={() => setShowAllFileTypes(!showAllFileTypes)}
              onParseSelected={handleParseSelectedDriveFiles}
            />

            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setStep('drive_input')
                  setDriveFolderFiles([])
                  setDriveJobGroups([])
                  setShowAllFileTypes(false)
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                ← Back
              </button>
            </div>
          </div>
        )}

        {/* Step 3c: Drive Confirm */}
        {step === 'drive_confirm' && mode && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Confirm Job Groups</h2>

            <div className="space-y-4 mb-6">
              {driveJobGroups.map((group, idx) => {
                const counts: Record<string, number> = {}
                group.files.forEach(f => {
                  counts[f.role] = (counts[f.role] || 0) + 1
                })
                const summary = Object.entries(counts)
                  .map(([role, c]) => `${c} ${role.toUpperCase()}`)
                  .join(' + ')

                return (
                  <div key={group.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <p className="font-medium text-gray-900 dark:text-white">Job {idx + 1}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{summary}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                      Files: {group.files.map(f => f.name).join(', ')}
                    </p>
                  </div>
                )
              })}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('drive_preview')}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={() => processDriveJobGroup(0)}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Start Processing ({driveJobGroups.length} job{driveJobGroups.length !== 1 ? 's' : ''})
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Upload */}
        {/* LOCAL UPLOAD (Step 3, local source). Shown for single and mbl_hbl ONLY (lib/autoJobsModes.ts). For manifest, bundled and invoices the entry was REMOVED 2026-11-05 and
            this block is unreachable for them; their branches stay in the code for reference. WHY they were switched off: a file uploaded here was parsed IN THE BROWSER
            while the same file from Google Drive goes to the server (parse-document), and the results differ: manifest (confirmed): the server reads the importer's EORI from
            column I, the browser parser does not read column I at all (it calls it isLocal) and starts its rows one row earlier; bundled: the browser takes a whole cell as
            the company name, the server splits name / address / EORI / contact; invoices: the browser reads HS-coded invoice lines, the server reads HBL fields. And, suspected
            (read from the code, NOT yet tried): after the browser-side parse the flow ends in 'hbl_summary', and nothing turns detectedHBLs into the job input that
            'address_validation' needs, so 'Create N jobs' may lead to a blank page for bundled / manifest / invoices. Before restoring any of them: docs/go_live_checklist.md section 8. */}
        {step === 'upload' && mode && supportsLocalUpload(mode) && (mode !== 'single' || fileFormat) && dataSource === 'local' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            {mode === 'mbl_hbl' ? (
              // MBL + HBL mode: Two upload zones
              <>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Upload Documents</h2>
                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* MBL Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Master Bill of Lading
                    </label>
                    <div
                      onClick={() => document.getElementById('mbl-input')?.click()}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => {
                        e.preventDefault()
                        const dropped = Array.from(e.dataTransfer.files)
                        if (dropped[0]) setMblFile(dropped[0])
                      }}
                      className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                    >
                      <input
                        id="mbl-input"
                        type="file"
                        accept=".pdf,.xlsx,.xls"
                        onChange={e => {
                          if (e.target.files?.[0]) setMblFile(e.target.files[0])
                        }}
                        className="hidden"
                      />
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {mblFile ? mblFile.name : 'Drop file here'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">PDF or Excel</p>
                    </div>
                  </div>

                  {/* HBL Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      House Bill of Lading
                    </label>
                    <div
                      onClick={() => document.getElementById('hbl-input')?.click()}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => {
                        e.preventDefault()
                        const dropped = Array.from(e.dataTransfer.files)
                        if (dropped[0]) setHblFile(dropped[0])
                      }}
                      className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                    >
                      <input
                        id="hbl-input"
                        type="file"
                        accept=".pdf,.xlsx,.xls"
                        onChange={e => {
                          if (e.target.files?.[0]) setHblFile(e.target.files[0])
                        }}
                        className="hidden"
                      />
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {hblFile ? hblFile.name : 'Drop file here'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">PDF or Excel</p>
                    </div>
                  </div>
                </div>

                {mblFile && hblFile && (
                  <>
                    <button
                      onClick={handleProcess}
                      disabled={isParsing}
                      className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors mb-4"
                    >
                      {isParsing ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Parsing...
                        </span>
                      ) : (
                        'Process Files'
                      )}
                    </button>
                    {isParsing && (
                      <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-4">
                        This may take 15-20 seconds
                      </p>
                    )}
                  </>
                )}

                <button
                  onClick={() => {
                    setStep('format')
                    setMblFile(null)
                    setHblFile(null)
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  ← Back
                </button>
              </>
            ) : (
              // Single file modes
              <>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault()
                    const dropped = Array.from(e.dataTransfer.files)
                    setFiles(dropped)
                  }}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center cursor-pointer hover:border-blue-400 transition-colors"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={getFileAccept()}
                    onChange={e => setFiles(Array.from(e.target.files || []))}
                    className="hidden"
                  />
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <p className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4">
                    {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : `Drop ${getFormatDescription()} here`}
                  </p>
                  {files.length > 0 ? (
                    <div className="space-y-2 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg mb-4">
                      {files.map((file, idx) => (
                        <div key={`${file.name}-${idx}`} className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                          <span className="truncate">
                            📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              setFiles(files.filter((_, i) => i !== idx))
                            }}
                            className="ml-2 text-red-500 hover:text-red-700 font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">or click to browse</p>
                  )}
                </div>

                {files.length > 0 && (
                  <>
                    <button
                      onClick={handleProcess}
                      disabled={isParsing}
                      className="mt-6 w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                    >
                      {isParsing ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Parsing...
                        </span>
                      ) : files.length > 1 ? (
                        'Process Files'
                      ) : (
                        'Process File'
                      )}
                    </button>
                    {isParsing && (
                      <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-3">
                        This may take 15-20 seconds
                      </p>
                    )}
                  </>
                )}

                <button
                  onClick={() => {
                    setStep('format')
                    setFiles([])
                  }}
                  className="mt-4 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  ← Back
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 4: Processing */}
        {step === 'upload' && progress && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">{progress}</p>
          </div>
        )}

        {/* Step 5: HBL Summary (Multi-sheet Excel) */}
        {step === 'hbl_summary' && detectedHBLs.length > 0 && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              {detectedHBLs[0]?.hawbNumber ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Air Manifest - {detectedHBLs[0]?.mawbNumber || 'MAWB'}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Found {detectedHBLs.length} House Air Waybills. Select which ones to create as jobs.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Detected HBLs</h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Found {detectedHBLs.length} House Bills of Lading. Select which ones to create as jobs.
                  </p>
                </>
              )}

              {/* Compact table for multi-HBL (5+ items) */}
              {detectedHBLs.length >= 5 && !detectedHBLs[0]?.hawbNumber && (
                <>
                  {/* Common Fields Summary */}
                  <div className="mb-6 grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm">
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Vessel:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{detectedHBLs[0]?.vessel || '—'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">POL:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{detectedHBLs[0]?.ports?.loading || '—'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">POD:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{detectedHBLs[0]?.ports?.discharge || '—'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Container:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{detectedHBLs[0]?.containerNumber || '—'}</span>
                    </div>
                  </div>

                  {/* Compact HBL Table */}
                  <div className="overflow-x-auto mb-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <tr>
                          <th className="px-3 py-2 text-left">
                            <input
                              type="checkbox"
                              checked={selectedHBLsForCreation.size === detectedHBLs.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedHBLsForCreation(new Set(detectedHBLs.map(h => h.sheetName)))
                                } else {
                                  setSelectedHBLsForCreation(new Set())
                                }
                              }}
                              title="Select all HBLs"
                            />
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">#</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">HBL No</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">Consignee</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">EORI</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">Cartons</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">Cargo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {detectedHBLs.map((hbl, idx) => (
                          <tr key={hbl.sheetName} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedHBLsForCreation.has(hbl.sheetName)}
                                onChange={(e) => {
                                  const newSelected = new Set(selectedHBLsForCreation)
                                  if (e.target.checked) {
                                    newSelected.add(hbl.sheetName)
                                  } else {
                                    newSelected.delete(hbl.sheetName)
                                  }
                                  setSelectedHBLsForCreation(newSelected)
                                }}
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-900 dark:text-white">{idx + 1}</td>
                            <td className="px-3 py-2 font-mono text-gray-900 dark:text-white">{hbl.hblNumber || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400 truncate max-w-xs">{hbl.consignee || '—'}</td>
                            <td className="px-3 py-2 font-mono text-gray-900 dark:text-white">{hbl.consigneeEori || '—'}</td>
                            <td className="px-3 py-2 text-gray-900 dark:text-white">{hbl.pieces || hbl.packages || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400 truncate max-w-xs">{hbl.cargoDescription || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Selection Summary */}
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {selectedHBLsForCreation.size} of {detectedHBLs.length} HBLs selected
                  </div>
                </>
              )}

              {/* Full Table for other formats or small number of items */}
              {detectedHBLs.length < 5 || detectedHBLs[0]?.hawbNumber && (
                <div className="overflow-x-auto mb-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={selectedHBLsForCreation.size === detectedHBLs.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedHBLsForCreation(new Set(detectedHBLs.map(h => h.sheetName)))
                              } else {
                                setSelectedHBLsForCreation(new Set())
                              }
                            }}
                          />
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">
                          {detectedHBLs[0]?.hawbNumber ? 'HAWB' : 'Sheet'}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">
                          {detectedHBLs[0]?.hawbNumber ? 'HAWB No' : 'BL No'}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Consignee</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">
                          {detectedHBLs[0]?.hawbNumber ? 'Origin' : 'POL'}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">
                          {detectedHBLs[0]?.hawbNumber ? 'Destination' : 'POD'}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Packages</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">
                          {detectedHBLs[0]?.hawbNumber ? 'Weight (kg)' : 'Container'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {detectedHBLs.map((hbl) => (
                        <tr key={hbl.sheetName} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedHBLsForCreation.has(hbl.sheetName)}
                              onChange={(e) => {
                                const newSelected = new Set(selectedHBLsForCreation)
                                if (e.target.checked) {
                                  newSelected.add(hbl.sheetName)
                                } else {
                                  newSelected.delete(hbl.sheetName)
                                }
                                setSelectedHBLsForCreation(newSelected)
                              }}
                            />
                          </td>
                          <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">
                            {hbl.hawbNumber || hbl.sheetName}
                          </td>
                          <td className="px-4 py-3 text-gray-900 dark:text-white">
                            {hbl.hawbNumber || hbl.hblNumber || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{hbl.consignee || '—'}</td>
                          <td className="px-4 py-3 text-gray-900 dark:text-white">
                            {hbl.origin || hbl.ports?.loading || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-900 dark:text-white">
                            {hbl.destination || hbl.ports?.discharge || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{hbl.packages || '—'}</td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                            {hbl.hawbNumber
                              ? hbl.grossWeightKgs
                                ? `${hbl.grossWeightKgs} kg`
                                : '—'
                              : hbl.containerNumber || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setStep('upload')
                    setDetectedHBLs([])
                    setSelectedHBLsForCreation(new Set())
                  }}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (selectedHBLsForCreation.size > 0) {
                      // Phase 2 flow: all modes go through address validation
                      setStep('address_validation')
                    }
                  }}
                  disabled={selectedHBLsForCreation.size === 0}
                  className={`flex-1 py-3 font-semibold rounded-lg transition-colors ${
                    selectedHBLsForCreation.size > 0
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Create {selectedHBLsForCreation.size} Job{selectedHBLsForCreation.size !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Review */}
        {step === 'review' && editData && detectedHBLs.length === 0 && (
          <div className="space-y-4">
            <MBLHBLTable
              mode={mode || 'single'}
              transportMode={(editData.transport_mode || 'SEA').toUpperCase()}
              masterRow={masterRowData ? extractedToDetailRow(masterRowData, 'master') : extractedToDetailRow(editData, 'master')}
              detailRows={detailRowsData.length > 0 ? detailRowsData.map((row, idx) => extractedToDetailRow(row, `detail-${idx}`)) : []}
              containerTypeOptions={containerTypes.map(t => ({ value: t.code, label: t.boxtop_name ? `${t.boxtop_name} (${t.code})` : t.code }))}
              shippingTermOptions={shippingTerms.map(t => ({ value: t.code, label: t.description ? `${t.code} - ${t.description}` : t.code }))}
              onDataChange={(rows, master) => {
                // #297: MBLHBLTable owns per-row edit state internally — this is where those
                // edits (container type, freight term, and everything else double-click-editable)
                // actually reach the data that gets sent to create-autojobs on Continue.
                setDetailRowsData(rows.map((row, idx) => detailRowToExtracted(row, detailRowsData[idx])))
                if (master) {
                  if (masterRowData) setMasterRowData(detailRowToExtracted(master, masterRowData))
                  else setEditedData(detailRowToExtracted(master, editData))
                }
              }}
              onContinue={() => {
                // Phase 2 flow: all modes go through address validation
                setStep('address_validation')
              }}
            />
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setStep('upload')
                  setDetectedHBLs([])
                  setSelectedHBLsForCreation(new Set())
                }}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Step 7: Job Reference */}
        {step === 'jobref' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Enter Job Reference</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Each job needs a unique reference number. You can modify the suggested format.
            </p>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Job Reference (Required)
              </label>
              <input
                type="text"
                value={jobRef}
                onChange={e => setJobRef(e.target.value)}
                placeholder="e.g., KI-260503-001"
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-lg font-mono focus:border-blue-500 focus:outline-none"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Format: KI-YYMMDD-NNN (e.g., KI-260503-001 for May 3, 2026, job #001)
              </p>
            </div>

            {/* Document Preview */}
            {editData && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">Document Summary</h3>
                <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                  <p><span className="font-medium">Type:</span> {editData.doc_type || 'Unknown'}</p>
                  <p><span className="font-medium">Transport:</span> {editData.transport_mode || 'Unknown'} / {editData.direction || 'Unknown'}</p>
                  <p><span className="font-medium">BL/AWB:</span> {editData.mbl_number || editData.waybill_number || 'Not extracted'}</p>
                  <p><span className="font-medium">Vessel/Flight:</span> {editData.vessel_name || editData.flight_vessel || 'Not extracted'}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex gap-4">
              <button
                onClick={() => setStep('review')}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleCreateJob}
                disabled={!jobRef.trim()}
                className={`flex-1 py-3 font-semibold rounded-lg transition-colors ${
                  jobRef.trim()
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                Create Job
              </button>
            </div>
          </div>
        )}

        {/* Step 8: Creating */}
        {step === 'creating' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">{progress || 'Creating job...'}</p>
          </div>
        )}

        {/* Step 9: Done */}
        {step === 'done' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">Job created successfully!</p>
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                {jobRef && <span>Reference: <span className="font-mono font-semibold">{jobRef}</span></span>}
                {boxtraxSyncStatus === 'success' && boxtraxRef && (
                  <span className="ml-2 text-green-600 dark:text-green-400 font-medium">
                    + Synced to BoxTop as <span className="font-mono">{boxtraxRef}</span>
                  </span>
                )}
                {(boxtraxSyncStatus === 'warning' || boxtraxSyncStatus === 'error') && (
                  <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">
                    ⚠ BoxTop sync pending
                  </span>
                )}
              </p>
            </div>

            {/* Boxtrax Integration Status */}
            {boxtraxSyncStatus === 'syncing' && (
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl shadow-sm border border-blue-200 dark:border-blue-800 p-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-blue-200 border-t-white rounded-full animate-spin"></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Syncing to Boxtrax...</h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      Sending shipment data to Boxtrax for tracking integration.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {boxtraxSyncStatus === 'success' && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl shadow-sm border border-green-200 dark:border-green-800 p-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">✅ Synced to Boxtrax</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-2">
                      Job successfully synced to Boxtrax for tracking.
                    </p>
                    {boxtraxRef && (
                      <p className="text-sm font-mono bg-white dark:bg-gray-700 px-3 py-2 rounded border border-green-200 dark:border-green-700 text-green-800 dark:text-green-200">
                        Boxtrax Ref: {boxtraxRef}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(boxtraxSyncStatus === 'warning' || boxtraxSyncStatus === 'error') && (
              <div className={`bg-gradient-to-r ${boxtraxSyncStatus === 'warning' ? 'from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20' : 'from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20'} rounded-xl shadow-sm border ${boxtraxSyncStatus === 'warning' ? 'border-yellow-200 dark:border-yellow-800' : 'border-red-200 dark:border-red-800'} p-8`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 ${boxtraxSyncStatus === 'warning' ? 'bg-yellow-600' : 'bg-red-600'} rounded-full flex items-center justify-center`}>
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold mb-2 ${boxtraxSyncStatus === 'warning' ? 'text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}`}>
                      {boxtraxSyncStatus === 'warning' ? '⚠️ Boxtrax Sync - Partial' : '❌ Boxtrax Sync Failed'}
                    </h3>
                    <p className={`${boxtraxSyncStatus === 'warning' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-700 dark:text-gray-300'} mb-2`}>
                      {boxtraxError || 'There was an issue syncing to Boxtrax.'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Job was successfully created in CYUK. You can retry Boxtrax sync from the job detail page.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => createdJobId && navigate(`/jobs/${createdJobId}`)}
                disabled={!createdJobId}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                View Job
              </button>
              <button
                onClick={resetFormForNewJob}
                className="px-8 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Create Another
              </button>
            </div>
          </div>
        )}

        {/* Address/Customer Check Modal */}
        {addressCheckModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Customer Information</h2>
                <button
                  onClick={() => {
                    setAddressCheckModal(false)
                    setPendingCustomers([])
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                <p className="text-gray-600 dark:text-gray-400">
                  Some customer information from the document is not found in our system. Please choose an action for each:
                </p>

                {pendingCustomers.map((pending) => (
                  <div key={pending.type} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="mb-4">
                      <h3 className="font-semibold text-gray-900 dark:text-white capitalize">
                        {pending.type === 'shipper' ? 'Shipper' : 'Consignee'}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{pending.name}</p>
                    </div>

                    <div className="space-y-2">
                      {pending.matchedCustomers.length > 0 ? (
                        <>
                          <label className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                            <input
                              type="radio"
                              name={`action-${pending.type}`}
                              value="select"
                              checked={customerAction[pending.type] === 'select'}
                              onChange={() => setCustomerAction(prev => ({ ...prev, [pending.type]: 'select' }))}
                              className="w-4 h-4 text-blue-600"
                            />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">Use Existing Customer</div>
                              <div className="mt-1">
                                <SearchableSelect
                                  options={pending.matchedCustomers.map(c => ({
                                    id: c.id,
                                    label: `${c.code} - ${c.name}`
                                  }))}
                                  value={selectedCustomers[pending.type] || ''}
                                  onChange={(value) => setSelectedCustomers(prev => ({ ...prev, [pending.type]: value }))}
                                  disabled={customerAction[pending.type] !== 'select'}
                                  placeholder="Search customer by code or name..."
                                />
                              </div>
                            </div>
                          </label>
                        </>
                      ) : null}

                      <label className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                        <input
                          type="radio"
                          name={`action-${pending.type}`}
                          value="create"
                          checked={customerAction[pending.type] === 'create'}
                          onChange={() => setCustomerAction(prev => ({ ...prev, [pending.type]: 'create' }))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">Create New Customer</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Name: {pending.name}
                            {pending.type === 'shipper' && editData?.ports?.loading && (
                              <div>Location: {editData.ports.loading}</div>
                            )}
                            {pending.type === 'consignee' && editData?.ports?.discharge && (
                              <div>Location: {editData.ports.discharge}</div>
                            )}
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                        <input
                          type="radio"
                          name={`action-${pending.type}`}
                          value="skip"
                          checked={customerAction[pending.type] === 'skip'}
                          onChange={() => setCustomerAction(prev => ({ ...prev, [pending.type]: 'skip' }))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">Skip for Now</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Add customer later from the job details</div>
                        </div>
                      </label>
                    </div>
                  </div>
                ))}

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setAddressCheckModal(false)
                      setPendingCustomers([])
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddressModalSubmit}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Address Validation */}
        {step === 'address_validation' && (masterRowData || editedData || extractedData) && (
          <AddressValidationStep
            // Z55: this used to skip `editedData`, so for 'single' mode (which never calls
            // setMasterRowData -- see the Z48/Z43 note on handleJobConfirm's own baseMaster
            // below) it validated addresses against the stale pre-edit extractedData while
            // create-autojobs got editedData. Any wizard-step correction to the shipper/
            // consignee name meant the addressDecisions keys (built from extractedData's
            // name) never matched the customerMap keys create-autojobs computed from
            // editedData's name, so importer_id/consignor_id/notify_id all came back null.
            master={masterRowData || editedData || extractedData!}
            details={detailRowsData}
            onContinue={handleAddressDecisions}
            onBack={() => {
              // Route back to appropriate step based on mode and state
              if (detectedHBLs.length > 0) {
                setStep('hbl_summary')
              } else {
                setStep('review')
              }
            }}
          />
        )}

        {/* Step 6: Job Creation Confirm Dialog */}
        {showJobConfirmDialog && (masterRowData || editedData || extractedData) && (
          <JobCreationConfirmDialog
            mode={mode || 'single'}
            parentRef={plannedParentJobRef || ''}
            // Z55: same stale-extractedData fallback bug as AddressValidationStep above --
            // this dialog's own carrier/port pre-fill values must be seeded from the same
            // master handleJobConfirm will actually send.
            master={masterRowData || editedData || extractedData!}
            details={detailRowsData}
            customersCreatedCount={addressDecisions.filter(d => d.action === 'create_new').length}
            carriers={shippingLines}
            ports={ports}
            customers={customerOptions}
            userOptions={autoJobUsers}
            currentUserEmail={userEmail || ''}
            shedOptions={sheds.map(s => ({ value: s.id, label: s.name ? `${s.code} - ${s.name}` : s.code }))}
            onConfirm={handleJobConfirm}
            onCancel={() => setShowJobConfirmDialog(false)}
            isLoading={jobCreationLoading}
          />
        )}

        {/* Step 7: Job Creation Result */}
        {step === 'job_result' && createdParentJobRef && (
          <JobCreationResultStep
            parentJobId={createdParentJobId || ''}
            parentJobRef={createdParentJobRef}
            childJobIds={createdChildJobIds}
            childJobRefs={createdChildJobRefs}
            customersCreated={createdCustomers}
            onCreateAnother={handleStartOver}
          />
        )}

        {/* Z58.7: consolidated Sync Preflight -- InvoiceTo/Shipper/Consignee/Notify all shown,
            every party has a visible status, "Approve All & Sync" only enabled once every
            needs_decision party is resolved. */}
        {BOXTOP_SYNC_ENABLED && pendingPreflight && (
          <SyncPreflightModal
            jobRef={jobRef}
            onCancel={() => {
              setPendingPreflight(null)
              setBoxtraxSyncStatus('error')
              setBoxtraxError('Boxtrax sync skipped — you can retry from the job detail page.')
            }}
            onApproved={() => {
              const { jobId } = pendingPreflight
              setPendingPreflight(null)
              setBoxtraxSyncStatus('syncing')
              syncJobToBoxtrax(jobId, true)
            }}
          />
        )}
      </div>
    </div>
  )
}
